import React, { useState, useMemo, useEffect } from 'react';
import agentsConfig from '../agentsConfig.json';
import {
  UserCheck, Search, Mail, Globe, Download, Copy, Check,
  RefreshCw, Filter, ShieldAlert, Sparkles, FileText, ArrowLeft,
  Users, CheckCircle2, AlertCircle, Trash2, ExternalLink, Send,
  Upload, FileSpreadsheet, UserPlus, ListPlus, Flag, Languages, MapPin, Briefcase,
  Zap, Rocket, Play, BarChart3, CheckCircle, Clock, X, ChevronRight, Award, ShieldCheck, MailCheck,
  GraduationCap, Scale, HardHat, Truck, Stethoscope, Wheat, Building2, Landmark, CheckSquare, Square, ChevronDown, ChevronUp, Sliders
} from 'lucide-react';

interface AdminLeadHarvesterProps {
  user: any;
  language: 'uk' | 'en' | 'pl';
  onBackToHub: () => void;
}

interface HarvestedLead {
  email: string;
  name_or_title: string;
  source_url: string;
  profession: string;
  city_or_region: string;
  snippet: string;
  region?: 'ukraine' | 'poland' | 'eu';
  language?: 'uk' | 'pl' | 'en';
}

interface ForumLead {
  title: string;
  url: string;
  topic: string;
  audience_type: string;
  estimated_activity: string;
  description: string;
}

export interface AutopilotSectorReport {
  sectorName: string;
  discoveredCount: number;
  dispatchedCount: number;
  status: 'success' | 'no_leads' | 'already_sent' | 'error';
  note?: string;
  dispatchedEmails: string[];
}

export interface AutopilotSummaryReport {
  targetRegion: 'ukraine' | 'poland' | 'eu';
  language: 'uk' | 'pl' | 'en';
  startedAt: string;
  completedAt: string;
  totalDiscovered: number;
  totalDispatched: number;
  sectors: AutopilotSectorReport[];
}

export interface AutopilotProfessionGroup {
  id: string;
  iconName: string;
  titleUk: string;
  titlePl: string;
  titleEn: string;
  rolesUk: string[];
  rolesPl: string[];
  rolesEn: string[];
}

export const AUTOPILOT_PROFESSION_GROUPS: AutopilotProfessionGroup[] = [
  {
    id: 'legal_notary',
    iconName: 'Scale',
    titleUk: '⚖️ Юридичний блок, Адвокатура & Нотаріат',
    titlePl: '⚖️ Prawo, Adwokatura & Notariat',
    titleEn: '⚖️ Legal, Advocacy & Notary Public',
    rolesUk: [
      'Юрист / Адвокат (комерційні договори за ЦКУ)',
      'Нотаріус / Нотаріальна контора (договори, довіреності, посвідчення)',
      'Корпоративний юрист / Реєстрація ТОВ, статутні документи',
      'Арбітражний керуючий / Юрист з банкрутства',
      'Судовий адвокат / Господарські та податкові спори',
      'Патентний повірений / Юрист з інтелектуальної власності',
      'Юрист з міжнародного комерційного арбітражу (GAFTA, FOSFA, ICC)'
    ],
    rolesPl: [
      'Radca Prawny / Kancelaria Prawna (KSH, Umowy B2B)',
      'Adwokat / Kancelaria Adwokacka (Spory Sądowe, Prawo Gospodarcze)',
      'Notariusz / Kancelaria Notarialna (Akty Notarialne, Poświadczenia)',
      'Rzecznik Patentowy / Prawnik IP (Własność Intelektualna)',
      'Doradca Restrukturyzacyjny / Syndyk (Upadłość i Restrukturyzacja)',
      'Prawnik ds. Arbitrażu Handlowego (ICC Paris, GAFTA, Demurrage)'
    ],
    rolesEn: [
      'Cross-Border Corporate Counsel & Commercial Lawyer',
      'Notary Public & Civil Law Notary (Contracts, Certifications)',
      'Litigation & Dispute Resolution Attorney',
      'Patent & Trademark Attorney (EUIPO / EPO)',
      'International Trade Arbitrator & Commodities Lawyer (GAFTA, FOSFA, ICC)'
    ]
  },
  {
    id: 'science_academia',
    iconName: 'GraduationCap',
    titleUk: '🎓 Наука, R&D & Вища Освіта',
    titlePl: '🎓 Nauka, R&D & Szkolnictwo Wyższe',
    titleEn: '🎓 Science, R&D & Higher Education',
    rolesUk: [
      'Ректорат / Деканат / Університет (акредитація НАЗЯВО, освітні програми)',
      'Професор / Доктор наук / Завідувач кафедри (Scopus, WoS, статті)',
      'Науково-дослідний інститут / Лабораторія (гранти НФДУ, НДДКР)',
      'Аспірант / Докторант / Вчена рада (оформлення дисертацій за ДСТУ)',
      'Керівник наукового гранту / R&D директор (Horizon Europe, НФДУ)'
    ],
    rolesPl: [
      'Uczelnia Wyższa / Rektorat / Dziekanat (Akredytacja PKA, Programy Studiów)',
      'Profesor / Doktor Habilitowany / Kierownik Katedry (Scopus, NCN/NCBR)',
      'Instytut Badawczy / Laboratorium R&D (Projekty B+R, Publikacje)',
      'Doktorant / Szkoła Doktorska (Prace Doktorskie, Formatowanie)'
    ],
    rolesEn: [
      'University Faculty & Academic Department Head (Curriculum & Accreditation)',
      'Principal Investigator & Research Scientist (Horizon Europe, ERC Grants)',
      'PhD Researcher & Postdoctoral Scholar (Scientific Publications & Data)'
    ]
  },
  {
    id: 'tax_accounting',
    iconName: 'Calculator',
    titleUk: '🏛️ Податки, Бухгалтерія & Фінанси',
    titlePl: '🏛️ Podatki, Księgowość & Finanse',
    titleEn: '🏛️ Tax, Accounting & Finance',
    rolesUk: [
      'Головний бухгалтер / Аудитор (ПКУ, ДПС, розблокування ПН СМКОР)',
      'Податковий консультант / Аудиторська фірма',
      'Фінансовий директор (CFO) / Фінансовий аналітик',
      'Бухгалтер бюджетної установи (НПСБОДС, КЕКВ, Казначейство)',
      'Бухгалтер ФОП та малого бізнесу (єдиний податок, РРО)'
    ],
    rolesPl: [
      'Biuro Rachunkowe / Główny Księgowy (KSeF, FA_VAT, JPK_V7)',
      'Doradca Podatkowy / Kancelaria Podatkowa (CIT Estoński, VAT)',
      'Dyrektor Finansowy (CFO) / Analityk Finansowy',
      'Biegły Rewident / Firma Audytorska (Badanie Sprawozdań)'
    ],
    rolesEn: [
      'Corporate Accountant & Tax Advisor (EU VAT, OSS, IFRS)',
      'Chartered Certified Accountant & Financial Controller (CFO)',
      'Statutory Auditor & Audit Firm'
    ]
  },
  {
    id: 'construction_bim',
    iconName: 'HardHat',
    titleUk: '🏗️ Будівництво, Архітектура & Кошториси',
    titlePl: '🏗️ Budownictwo, Architektura & Kosztorysy',
    titleEn: '🏗️ Construction, Architecture & Cost Estimating',
    rolesUk: [
      'Інженер-кошторисник (АВК-5, ДБН, РЕКН, КБ-2в/КБ-3)',
      'Головний інженер проекту (ГІП) / Начальник ВТВ (ДБН А.3.1-5:2016)',
      'Архітектор / Проектна організація (ДІАМ, ЄДЕССБ)',
      'Інженер технагляду / Будівельний експерт (обстеження пошкоджень)',
      'BIM-менеджер / Проектувальник (Revit, IFC, обміри)'
    ],
    rolesPl: [
      'Kosztorysant Budowlany (KNR, SEKOCENBUD, Przedmiar Robót)',
      'Kierownik Budowy / Inspektor Nadzoru Inwestorskiego (Dziennik Budowy)',
      'Architekt / Biuro Projektowe (Prawo Budowlane, Pozwolenia)',
      'Inżynier BIM / Koordynator Projektu (IFC, Obmiary)'
    ],
    rolesEn: [
      'Construction Project Manager & Quantity Surveyor (BOQ, FIDIC)',
      'Architectural & Structural Engineering Lead (Eurocodes)',
      'BIM Coordinator & Construction Cost Estimator'
    ]
  },
  {
    id: 'logistics_customs',
    iconName: 'Truck',
    titleUk: '🚢 Логістика, Митниця & ЗЕД',
    titlePl: '🚢 Logistyka, Cło & TSL',
    titleEn: '🚢 Logistics, Customs & Freight Forwarding',
    rolesUk: [
      'Митний брокер / Декларант (УКТ ЗЕД, МД-2, Митниця)',
      'Міжнародний експедитор / Логістична компанія (CMR, ТТН, Incoterms)',
      'Менеджер ЗЕД / Імпортер / Експортер'
    ],
    rolesPl: [
      'Agencja Celna / Agent Celny (SENT, SAD, Procedury Celne)',
      'Spedytor Międzynarodowy / Dyspozytor Transportu (CMR, TSL)',
      'Kierownik Logistyki / Dyrektor ds. Eksportu i Importu'
    ],
    rolesEn: [
      'International Freight Forwarder & Customs Broker (TARIC, Transit)',
      'Supply Chain & Logistics Director (Incoterms 2020)'
    ]
  },
  {
    id: 'pharma_healthcare',
    iconName: 'Stethoscope',
    titleUk: '💊 Медицина, Фармацевтика & Аптеки',
    titlePl: '💊 Farmacja, Medycyna & Apteki',
    titleEn: '💊 Pharmaceuticals & Healthcare',
    rolesUk: [
      'Спеціаліст з реєстрації лікарських засобів (eCTD, ДЕЦ МОЗ)',
      'Директор аптечної мережі / Провізор (Доступні ліки, НСЗУ, ДЛУ)',
      'Фармацевтичний виробник / Менеджер з якості (GMP, GDP)'
    ],
    rolesPl: [
      'Specjalista ds. Rejestracji Leków (eCTD, URPL, EMA)',
      'Kierownik Apteki / Sieć Aptek (Refundacja NFZ, ChPL)',
      'Producent Farmaceutyczny / Osoba Wykwalifikowana (GMP/GDP)'
    ],
    rolesEn: [
      'Regulatory Affairs Director & Qualified Person (eCTD, EMA, GMP)',
      'Pharmacy Chain Director & Clinical Pharmacist'
    ]
  },
  {
    id: 'agri_cadastre',
    iconName: 'Wheat',
    titleUk: '🚜 Агросектор, Земельний Банк & Геодезія',
    titlePl: '🚜 Rolnictwo, Bank Gruntów & Kataster',
    titleEn: '🚜 Agribusiness, Land Bank & GIS Cadastre',
    rolesUk: [
      'Керівник агрохолдингу / Фермер (ДАР, субсидії, єдиний податок 4 група)',
      'Землевпорядник / Кадастровий інженер (ДЗК, накладки SHP/KML)',
      'Головний агроном / Агрохімік (NDVI, аналіз ґрунтів, урожайність)'
    ],
    rolesPl: [
      'Właściciel Gospodarstwa Rolnego / Agroholding (ARiMR, CAP Dotacje)',
      'Geodeta / Inżynier Katastru (EGiB, Działki Rolne)',
      'Doradca Rolniczy / Agronom (Ekoschematy, Nawożenie)'
    ],
    rolesEn: [
      'Agribusiness Executive & Farm Manager (EU CAP Subsidies)',
      'Land Surveyor & GIS Cadastre Specialist'
    ]
  },
  {
    id: 'energy_esg',
    iconName: 'Zap',
    titleUk: '⚡ Енергетика, ESG & CBAM',
    titlePl: '⚡ Energetyka, ESG & CBAM',
    titleEn: '⚡ Energy, ESG & CBAM Carbon Tax',
    rolesUk: [
      'Енергоаудитор / Енергоменеджер (ISO 50001, сертифікація будівель)',
      'Менеджер з декарбонізації & ESG (CBAM викиди CO2)',
      'Проектувальник СЕС, ВЕС & систем накопичення BESS'
    ],
    rolesPl: [
      'Audytor Energetyczny / Certyfikator Budynków (Świadectwa)',
      'Menedżer ESG & Śladu Węglowego (CBAM CO2, Raportowanie)',
      'Projektant Farm Fotowoltaicznych PV & Magazynów Energii BESS'
    ],
    rolesEn: [
      'Energy Auditor & ISO 50001 Lead Auditor',
      'Carbon Accounting & CBAM Compliance Officer',
      'Renewable Energy & BESS Storage Engineer'
    ]
  },
  {
    id: 'cyber_it',
    iconName: 'ShieldCheck',
    titleUk: '🔒 Кібербезпека, IT & Захист Інформації',
    titlePl: '🔒 Cyberbezpieczeństwo, IT & Ochrona Danych',
    titleEn: '🔒 Cybersecurity, IT & Data Protection',
    rolesUk: [
      'Керівник служби безпеки / CISO (КСЗІ, ДССЗЗІ, ISO 27001)',
      'IT-директор (CTO) / Системний архітектор',
      'DPO / Спеціаліст із захисту персональних даних'
    ],
    rolesPl: [
      'Oficer Bezpieczeństwa Informacji (CISO, NIS2, ISO 27001)',
      'Inspektor Ochrony Danych (IOD / RODO)',
      'Dyrektor IT (CTO) / Architekt Bezpieczeństwa'
    ],
    rolesEn: [
      'Chief Information Security Officer (CISO, NIS2, DORA)',
      'Data Protection Officer (GDPR Compliance)'
    ]
  },
  {
    id: 'procurement_tenders',
    iconName: 'Landmark',
    titleUk: '📋 Публічні Закупівлі & Тендери',
    titlePl: '📋 Zamówienia Publiczne & Przetargi',
    titleEn: '📋 Public Procurement & Tenders',
    rolesUk: [
      'Уповноважена особа із закупівель / Замовник (Prozorro, ДАСУ)',
      'Тендерний спеціаліст учасника / Оскарження в АМКУ'
    ],
    rolesPl: [
      'Specjalista ds. Zamówień Publicznych (PZP, MiniPortal, TED)',
      'Koordynator Ofert Przetargowych / Odwołania KIO'
    ],
    rolesEn: [
      'Public Procurement Officer & Tender Manager (TED Europe)'
    ]
  },
  {
    id: 'hr_valuation',
    iconName: 'Building2',
    titleUk: '🏢 Офіс, HR & Оцінка Майна',
    titlePl: '🏢 Kadry, HR & Wycena Majątku',
    titleEn: '🏢 Office, HR & Property Valuation',
    rolesUk: [
      'HR-директор / Спеціаліст кадрового діловодства (ДСТУ 4163)',
      'Оцінювач нерухомості та майна (ФДМУ, НСО)',
      'Рекрутер / Менеджер з підбору персоналу'
    ],
    rolesPl: [
      'Dyrektor HR / Specjalista ds. Kadr i Płac (ZUS, Kodeks Pracy)',
      'Rzeczoznawca Majątkowy (Wycena Nieruchomości)'
    ],
    rolesEn: [
      'Global Mobility & HR Director (Cross-Border Employment, A1)',
      'Certified Real Estate Appraiser & Property Valuator'
    ]
  }
];

export const getAllAutopilotRolesForRegion = (regionKey: 'ukraine' | 'poland' | 'eu'): string[] => {
  const list: string[] = [];
  AUTOPILOT_PROFESSION_GROUPS.forEach(g => {
    if (regionKey === 'poland') list.push(...g.rolesPl);
    else if (regionKey === 'eu') list.push(...g.rolesEn);
    else list.push(...g.rolesUk);
  });
  return list;
};

export default function AdminLeadHarvester({ user, language, onBackToHub }: AdminLeadHarvesterProps) {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isEn = language === 'en';
  const isAdmin = user?.role === 'admin' || ['bbass776@gmail.com', 'irinacosmos777@gmail.com'].includes(user?.email?.toLowerCase() || '');

  const [activeTab, setActiveTab] = useState<'gmail' | 'forums' | 'import'>('gmail');

  // Market & Region selection
  const [targetRegion, setTargetRegion] = useState<'ukraine' | 'poland' | 'eu'>(
    isPl ? 'poland' : isEn ? 'eu' : 'ukraine'
  );

  // Invitation language (automatically aligned with targetRegion, but admin can toggle)
  const [invitationLanguage, setInvitationLanguage] = useState<'uk' | 'pl' | 'en'>(
    isPl ? 'pl' : isEn ? 'en' : 'uk'
  );

  // Search parameters for Gmail
  const [profession, setProfession] = useState(
    targetRegion === 'poland' ? 'biuro rachunkowe' : targetRegion === 'eu' ? 'corporate accountant' : 'бухгалтер'
  );
  const [region, setRegion] = useState(
    targetRegion === 'poland' ? 'Polska' : targetRegion === 'eu' ? 'European Union' : 'Україна'
  );
  const [customKeywords, setCustomKeywords] = useState('');
  const [isHarvestingGmail, setIsHarvestingGmail] = useState(false);
  const [gmailResults, setGmailResults] = useState<HarvestedLead[]>([]);
  const [gmailSearchSummary, setGmailSearchSummary] = useState<string>('');

  // Search parameters for Forums
  const [forumTopic, setForumTopic] = useState(
    targetRegion === 'poland' ? 'księgowość i podatki KSeF' : targetRegion === 'eu' ? 'EU corporate tax and procurement' : 'бухгалтерія та аудит'
  );
  const [isHarvestingForums, setIsHarvestingForums] = useState(false);
  const [forumResults, setForumResults] = useState<ForumLead[]>([]);

  // Mass Import & Invite states
  const [importText, setImportText] = useState('');
  const [customNoteInput, setCustomNoteInput] = useState('');
  const [isImportingAndSending, setIsImportingAndSending] = useState(false);
  const [importResults, setImportResults] = useState<any[]>([]);

  // Autopilot States & Interactive Sector Selection
  const [selectedAutopilotRoles, setSelectedAutopilotRoles] = useState<string[]>(() =>
    getAllAutopilotRolesForRegion(isPl ? 'poland' : isEn ? 'eu' : 'ukraine')
  );
  const [showProfessionDrawer, setShowProfessionDrawer] = useState(false);
  const [professionSearchFilter, setProfessionSearchFilter] = useState('');
  const [isAutopilotRunning, setIsAutopilotRunning] = useState(false);
  const [autopilotCurrentSectorIndex, setAutopilotCurrentSectorIndex] = useState(0);
  const [autopilotTotalSectors, setAutopilotTotalSectors] = useState(0);
  const [autopilotCurrentSectorName, setAutopilotCurrentSectorName] = useState('');
  const [autopilotProgressStatus, setAutopilotProgressStatus] = useState('');
  const [autopilotReport, setAutopilotReport] = useState<AutopilotSummaryReport | null>(null);
  const [showAutopilotModal, setShowAutopilotModal] = useState(false);

  // System states
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [copiedAll, setCopiedAll] = useState(false);
  const [hasSearchedGmail, setHasSearchedGmail] = useState(false);

  // Invitation sending state
  const [sendingEmailIndex, setSendingEmailIndex] = useState<number | null>(null);
  const [isSendingAll, setIsSendingAll] = useState(false);
  const [sentMap, setSentMap] = useState<Record<string, boolean>>({});
  const [sendSuccessStatus, setSendSuccessStatus] = useState<string | null>(null);

  // Custom single email invitation state
  const [customEmailInput, setCustomEmailInput] = useState('');
  const [isSendingCustom, setIsSendingCustom] = useState(false);

  // Handle Region Switching
  const handleSelectTargetRegion = (reg: 'ukraine' | 'poland' | 'eu') => {
    setTargetRegion(reg);
    setSelectedAutopilotRoles(getAllAutopilotRolesForRegion(reg));
    if (reg === 'poland') {
      setInvitationLanguage('pl');
      setRegion('Polska');
      setProfession('biuro rachunkowe');
      setForumTopic('księgowość i podatki KSeF');
    } else if (reg === 'eu') {
      setInvitationLanguage('en');
      setRegion('European Union / EU');
      setProfession('corporate accountant');
      setForumTopic('EU tax, logistics and public procurement');
    } else {
      setInvitationLanguage('uk');
      setRegion('Україна');
      setProfession('бухгалтер');
      setForumTopic('бухгалтерія та аудит');
    }
    setGmailResults([]);
    setForumResults([]);
    setHasSearchedGmail(false);
    setSendSuccessStatus(null);
    setErrorMessage(null);
  };

  // Preset suggestions per region
  const professionPresets = {
    ukraine: [
      'Головний бухгалтер / Бухгалтер',
      'Юрист / Адвокат (господарське право)',
      'Нотаріус / Нотаріальна контора',
      'Професор / Доктор наук / Університет',
      'Інженер-кошторисник (АВК-5, ДБН)',
      'Митний брокер / Логіст (УКТ ЗЕД)',
      'Спеціаліст з реєстрації ліків (eCTD)',
      'Енергоаудитор / ESG & CBAM менеджер',
      'Керівник агропідприємства / Земельний банк'
    ],
    poland: [
      'Biuro Rachunkowe / Księgowy (KSeF)',
      'Radca Prawny / Kancelaria (KSH, Prawo Pracy)',
      'Notariusz / Kancelaria Notarialna',
      'Profesor / Uczelnia Wyższa / Granty',
      'Kierownik Budowy / Kosztorysant (KNR)',
      'Spedytor Międzynarodowy / Agencja Celna (SENT)',
      'Specjalista ds. Rejestracji Leków (eCTD)',
      'Audytor Energetyczny / ESG & CBAM',
      'Doradca Podatkowy / Dyrektor Finansowy (CFO)'
    ],
    eu: [
      'Enterprise Accountant / Tax Consultant (VAT EU)',
      'Cross-Border Corporate Counsel & Legal Advisor',
      'Notary Public & Civil Law Notary',
      'University Faculty / Research Scientist',
      'Construction Project Manager / FIDIC Engineer',
      'International Freight Forwarder / Customs Agent',
      'Regulatory Affairs Director (eCTD, EMA)',
      'Carbon & ESG Consultant (CBAM CO2)',
      'Public Procurement & Tender Specialist (TED EU)'
    ]
  };

  const forumPresets = {
    ukraine: [
      'бухгалтерія та податки ПКУ',
      'юридичний форум та судовий захист',
      'будівництво та кошториси ДБН',
      'міжнародні перевезення та ЗЕД'
    ],
    poland: [
      'księgowość, podatki i KSeF',
      'prawo gospodarcze i umowy B2B',
      'budownictwo, KNR i przetargi PZP',
      'transport międzynarodowy, SENT i CMR'
    ],
    eu: [
      'EU tax, cross-border VAT & compliance',
      'international commercial arbitration & law',
      'FIDIC construction contracts & engineering',
      'customs compliance & European logistics'
    ]
  };

  // Grouped agents from the platform for the dropdown selector
  const platformAgentCategories = useMemo(() => {
    const categoriesMap: Record<string, Array<{ id: string; name: string; targetRole: string }>> = {};

    (agentsConfig as any[]).forEach((agent) => {
      if (agent.id === 'admin-lead-harvester' || agent.id === 'enterprise-dashboard') return;

      const catKey = isPl
        ? (agent.categoryPl || agent.categoryEn || agent.categoryUk || 'INNE')
        : isEn
        ? (agent.categoryEn || agent.categoryUk || 'OTHER')
        : (agent.categoryUk || 'ІНШЕ');

      const agentDisplayName = isPl
        ? (agent.namePl || agent.nameUk)
        : isEn
        ? (agent.nameEn || agent.nameUk)
        : (agent.nameUk || agent.nameEn);

      let targetRole = agent.nameUk;
      if (targetRegion === 'poland') {
        targetRole = agent.namePl || agent.nameEn || agent.nameUk;
      } else if (targetRegion === 'eu') {
        targetRole = agent.nameEn || agent.nameUk;
      } else {
        targetRole = agent.nameUk || agent.nameEn;
      }

      if (!categoriesMap[catKey]) {
        categoriesMap[catKey] = [];
      }
      categoriesMap[catKey].push({
        id: agent.id,
        name: agentDisplayName,
        targetRole: targetRole
      });
    });

    return categoriesMap;
  }, [isPl, isEn, targetRegion]);

  // Flattened list for the datalist autocomplete
  const allPlatformAgentRoles = useMemo(() => {
    const list: string[] = [...professionPresets[targetRegion]];
    const autopilotRoles = getAllAutopilotRolesForRegion(targetRegion);
    autopilotRoles.forEach(r => {
      if (!list.includes(r)) list.push(r);
    });
    Object.values(platformAgentCategories).forEach(agents => {
      agents.forEach(a => {
        if (!list.includes(a.targetRole)) {
          list.push(a.targetRole);
        }
      });
    });
    return list;
  }, [platformAgentCategories, targetRegion]);

  // Autonomous Multi-Sector Harvest & Dispatch Engine
  const handleRunAutopilot = async () => {
    const allAvailableRoles = getAllAutopilotRolesForRegion(targetRegion);
    const sectors = selectedAutopilotRoles.length > 0 ? selectedAutopilotRoles : allAvailableRoles;

    if (sectors.length === 0) {
      setErrorMessage(
        isUk
          ? '⚠️ Оберіть хоча б одну професію для автопілоту!'
          : isPl
          ? '⚠️ Wybierz przynajmniej jeden zawód do autopilota!'
          : '⚠️ Please select at least one profession for the autopilot!'
      );
      return;
    }

    setIsAutopilotRunning(true);
    setShowAutopilotModal(true);
    setAutopilotReport(null);
    setAutopilotTotalSectors(sectors.length);
    setAutopilotCurrentSectorIndex(0);
    setAutopilotProgressStatus(
      isUk
        ? '🚀 Ініціалізація автономного циклу пошуку та розсилки...'
        : isPl
        ? '🚀 Inicjalizacja automatycznego cyklu wyszukiwania i wysyłki...'
        : '🚀 Initializing autonomous multi-sector harvest and dispatch...'
    );

    const startedAt = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const sectorReports: AutopilotSectorReport[] = [];
    const locallySentEmails = new Set<string>();

    for (let i = 0; i < sectors.length; i++) {
      const sectorName = sectors[i];
      setAutopilotCurrentSectorIndex(i + 1);
      setAutopilotCurrentSectorName(sectorName);
      setAutopilotProgressStatus(
        isUk
          ? `🔍 [${i + 1}/${sectors.length}] Сканування публічних контактів для: "${sectorName}"...`
          : isPl
          ? `🔍 [${i + 1}/${sectors.length}] Skanowanie kontaktów dla: "${sectorName}"...`
          : `🔍 [${i + 1}/${sectors.length}] Harvesting public contacts for: "${sectorName}"...`
      );

      try {
        // Step 1: Harvest emails for this sector
        const harvestRes = await fetch('/api/admin/harvest-gmail', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            profession: sectorName,
            region,
            targetRegion,
            language: invitationLanguage
          })
        });

        const harvestData = await harvestRes.json().catch(() => ({}));
        const rawLeads = Array.isArray(harvestData.leads) ? harvestData.leads : [];
        const extractedEmails: string[] = rawLeads
          .map((item: any) => (typeof item === 'string' ? item : item?.email || ''))
          .filter((e: string) => Boolean(e) && e.includes('@'))
          .map((e: string) => e.toLowerCase().trim());

        const uniqueExtracted = Array.from(new Set(extractedEmails));
        const newEmailsToSend = uniqueExtracted.filter(e => !sentMap[e] && !locallySentEmails.has(e));

        if (newEmailsToSend.length > 0) {
          setAutopilotProgressStatus(
            isUk
              ? `✉️ [${i + 1}/${sectors.length}] Надсилання персоналізованих пропозицій на ${newEmailsToSend.length} знайдених пошт...`
              : isPl
              ? `✉️ [${i + 1}/${sectors.length}] Wysyłanie spersonalizowanych zaproszeń na ${newEmailsToSend.length} adresów...`
              : `✉️ [${i + 1}/${sectors.length}] Dispatching personalized proposals to ${newEmailsToSend.length} emails...`
          );

          // Step 2: Send invitations to discovered new emails
          const sendRes = await fetch('/api/admin/send-lead-invitation', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              emails: newEmailsToSend,
              language: invitationLanguage,
              targetRegion
            })
          });

          await sendRes.json().catch(() => ({}));
          newEmailsToSend.forEach(e => {
            locallySentEmails.add(e);
            setSentMap(prev => ({ ...prev, [e]: true }));
          });

          sectorReports.push({
            sectorName,
            discoveredCount: uniqueExtracted.length,
            dispatchedCount: newEmailsToSend.length,
            status: 'success',
            dispatchedEmails: newEmailsToSend
          });
        } else {
          sectorReports.push({
            sectorName,
            discoveredCount: uniqueExtracted.length,
            dispatchedCount: 0,
            status: uniqueExtracted.length > 0 ? 'already_sent' : 'no_leads',
            note: uniqueExtracted.length > 0 
              ? (isUk ? 'Усі контакти вже запрошувалися раніше' : isPl ? 'Wszystkie kontakty zostały już wcześniej zaproszone' : 'All contacts were previously invited')
              : (isUk ? 'Нових публічних контактів не виявлено' : isPl ? 'Brak nowych kontaktów publicznych' : 'No new public contacts discovered'),
            dispatchedEmails: []
          });
        }
      } catch (err: any) {
        console.error(`Error in autopilot sector "${sectorName}":`, err);
        sectorReports.push({
          sectorName,
          discoveredCount: 0,
          dispatchedCount: 0,
          status: 'error',
          note: err?.message || 'Error occurred during processing',
          dispatchedEmails: []
        });
      }

      // Small cooldown between iterations for rate-limit protection
      await new Promise(r => setTimeout(r, 1500));
    }

    const completedAt = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const totalDiscovered = sectorReports.reduce((sum, s) => sum + s.discoveredCount, 0);
    const totalDispatched = sectorReports.reduce((sum, s) => sum + s.dispatchedCount, 0);

    const summaryReport: AutopilotSummaryReport = {
      targetRegion,
      language: invitationLanguage,
      startedAt,
      completedAt,
      totalDiscovered,
      totalDispatched,
      sectors: sectorReports
    };

    setAutopilotReport(summaryReport);
    setIsAutopilotRunning(false);
  };

  const downloadAutopilotReportCsv = () => {
    if (!autopilotReport) return;
    const header = 'ID,Sector,Discovered,Dispatched,Status,Details,TargetRegion,Language\n';
    const rows = autopilotReport.sectors.map((s, idx) => 
      `"${idx + 1}","${s.sectorName.replace(/"/g, '""')}","${s.discoveredCount}","${s.dispatchedCount}","${s.status}","${(s.note || s.dispatchedEmails.join(', ')).replace(/"/g, '""')}","${autopilotReport.targetRegion}","${autopilotReport.language}"`
    ).join('\n');
    
    const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `autopilot_report_${autopilotReport.targetRegion}_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string || '';
      const emailMatches = text.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g) || [];
      const uniqueEmails = Array.from(new Set(emailMatches.map(m => m.toLowerCase().trim())));
      if (uniqueEmails.length > 0) {
        setImportText(prev => {
          const existing = prev ? prev.split('\n').map(s => s.trim()) : [];
          const combined = Array.from(new Set([...existing, ...uniqueEmails]));
          return combined.join('\n');
        });
        setSendSuccessStatus(
          isUk
            ? `✅ Завантажено ${uniqueEmails.length} унікальних пошт з файлу "${file.name}"!`
            : isPl
            ? `✅ Załadowano ${uniqueEmails.length} unikalnych adresów email z pliku "${file.name}"!`
            : `✅ Loaded ${uniqueEmails.length} unique emails from "${file.name}"!`
        );
      } else {
        setErrorMessage(
          isUk
            ? `⚠️ У файлі "${file.name}" не знайдено жодної дійсної email-адреси.`
            : isPl
            ? `⚠️ W pliku "${file.name}" nie znaleziono żadnych prawidłowych adresów email.`
            : `⚠️ No valid emails found in "${file.name}".`
        );
      }
    };
    reader.readAsText(file);
  };

  const handleMassImportAndSend = async () => {
    const emailMatches = importText.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g) || [];
    const uniqueEmails = Array.from(new Set(emailMatches.map(m => m.toLowerCase().trim())));

    if (uniqueEmails.length === 0) {
      setErrorMessage(
        isUk
          ? 'Будь ласка, введіть або завантажте хоча б одну дійсну email-адресу.'
          : isPl
          ? 'Wprowadź lub załaduj co najmniej jeden prawidłowy adres email.'
          : 'Please enter or upload at least one valid email address.'
      );
      return;
    }

    setIsImportingAndSending(true);
    setSendSuccessStatus(null);
    setErrorMessage(null);

    try {
      const res = await fetch('/api/admin/send-lead-invitation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          emails: uniqueEmails,
          customNote: customNoteInput.trim() || undefined,
          language: invitationLanguage,
          targetRegion
        })
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || (isUk ? 'Помилка виконання масового імпорту.' : isPl ? 'Błąd wykonania importu masowego.' : 'Error executing mass import.'));
      }

      setImportResults(data.results || []);
      setSentMap(prev => {
        const next = { ...prev };
        uniqueEmails.forEach(e => { next[e] = true; });
        return next;
      });

      const langFlag = invitationLanguage === 'pl' ? '🇵🇱 Polski' : invitationLanguage === 'en' ? '🇬🇧 English' : '🇺🇦 Українська';
      setSendSuccessStatus(
        isUk
          ? `🎉 Успішно! Збережено у базі даних та надіслано запрошення (${langFlag}) на ${data.successCount || uniqueEmails.length} пошт!`
          : isPl
          ? `🎉 Sukces! Zapisano w bazie i wysłano zaproszenia (${langFlag}) na ${data.successCount || uniqueEmails.length} adresów!`
          : `🎉 Success! Saved to database and sent invitations (${langFlag}) to ${data.successCount || uniqueEmails.length} emails!`
      );
    } catch (err: any) {
      console.error(err);
      setErrorMessage(err.message || 'Error executing mass import');
    } finally {
      setIsImportingAndSending(false);
    }
  };

  const handleSendCustomEmail = async () => {
    const target = customEmailInput.trim();
    if (!target || !target.includes('@')) {
      setErrorMessage(
        isUk
          ? 'Будь ласка, введіть валідну email адресу (наприклад, user@gmail.com).'
          : isPl
          ? 'Wprowadź prawidłowy adres email (np. kontakt@firma.pl).'
          : 'Please enter a valid email address.'
      );
      return;
    }
    setIsSendingCustom(true);
    try {
      await handleSendInvitation([target]);
      setCustomEmailInput('');
    } finally {
      setIsSendingCustom(false);
    }
  };

  const safeHostname = (urlStr?: string): string => {
    if (!urlStr) return 'джерело';
    try {
      const formatted = urlStr.startsWith('http://') || urlStr.startsWith('https://') ? urlStr : `https://${urlStr}`;
      return new URL(formatted).hostname || urlStr;
    } catch {
      return urlStr;
    }
  };

  const handleToggleLeadRegion = async (index: number) => {
    const lead = gmailResults[index];
    if (!lead) return;

    const currentReg = lead.region || targetRegion;
    let nextReg: 'ukraine' | 'poland' | 'eu' = 'ukraine';
    let nextLang: 'uk' | 'pl' | 'en' = 'uk';

    if (currentReg === 'ukraine') {
      nextReg = 'poland';
      nextLang = 'pl';
    } else if (currentReg === 'poland') {
      nextReg = 'eu';
      nextLang = 'en';
    } else {
      nextReg = 'ukraine';
      nextLang = 'uk';
    }

    setGmailResults(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        region: nextReg,
        language: nextLang
      };
      return updated;
    });

    if (lead.email) {
      try {
        await fetch('/api/admin/update-lead-region', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: lead.email,
            region: nextReg,
            language: nextLang
          })
        });
      } catch (e) {
        console.warn('Could not sync lead region with DB:', e);
      }
    }
  };

  const handleSendInvitation = async (emails: string[], idx?: number) => {
    if (idx !== undefined) {
      setSendingEmailIndex(idx);
    } else {
      setIsSendingAll(true);
    }
    setSendSuccessStatus(null);
    setErrorMessage(null);

    try {
      if (idx !== undefined && gmailResults[idx]) {
        // Individual lead dispatch
        const lead = gmailResults[idx];
        const effectiveReg = lead.region || targetRegion;
        const effectiveLang = lead.language || (effectiveReg === 'poland' ? 'pl' : effectiveReg === 'eu' ? 'en' : 'uk');

        const res = await fetch('/api/admin/send-lead-invitation', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            emails: [lead.email],
            language: effectiveLang,
            targetRegion: effectiveReg
          })
        });

        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.error || (isUk ? 'Помилка надсилання пропозиції.' : isPl ? 'Błąd wysyłania zaproszenia.' : 'Error sending invitation.'));
        }

        const langLabel = effectiveLang === 'pl' ? '🇵🇱 Polski' : effectiveLang === 'en' ? '🇬🇧 English' : '🇺🇦 Українська';

        if (data.success) {
          setSentMap(prev => ({ ...prev, [lead.email]: true }));
          setSendSuccessStatus(
            isUk
              ? `✅ Пропозицію (${langLabel}) успішно надіслано через SMTP на ${lead.email} [${effectiveReg.toUpperCase()}]!`
              : isPl
              ? `✅ Zaproszenie (${langLabel}) pomyślnie wysłano na ${lead.email} [${effectiveReg.toUpperCase()}]!`
              : `✅ Proposal (${langLabel}) successfully sent to ${lead.email} [${effectiveReg.toUpperCase()}]!`
          );
        } else if (data.allLoggedLocally) {
          setSentMap(prev => ({ ...prev, [lead.email]: true }));
          setErrorMessage(
            isUk
              ? `⚠️ Увага: SMTP-сервер не підключено. Лист (${langLabel}) зафіксовано в системному лозі сервера (/logs/sent_emails.log).`
              : isPl
              ? `⚠️ Uwaga: Serwer SMTP nie jest podłączony. Wiadomość (${langLabel}) zapisano w logach (/logs/sent_emails.log).`
              : `⚠️ Warning: SMTP not connected. Email (${langLabel}) saved to local server logs.`
          );
        } else if (data.error) {
          setErrorMessage(`❌ ${data.error}`);
        }
      } else {
        // Bulk dispatch: group leads by their assigned region & language
        const leadsToSend = gmailResults.filter(l => emails.includes(l.email));
        const groups: Record<string, { reg: 'ukraine' | 'poland' | 'eu'; lang: 'uk' | 'pl' | 'en'; emails: string[] }> = {
          ukraine: { reg: 'ukraine', lang: 'uk', emails: [] },
          poland: { reg: 'poland', lang: 'pl', emails: [] },
          eu: { reg: 'eu', lang: 'en', emails: [] }
        };

        if (leadsToSend.length > 0) {
          leadsToSend.forEach(l => {
            const reg = l.region || targetRegion;
            const lang = l.language || (reg === 'poland' ? 'pl' : reg === 'eu' ? 'en' : 'uk');
            if (!groups[reg]) {
              groups[reg] = { reg, lang, emails: [] };
            }
            groups[reg].emails.push(l.email);
          });
        } else {
          groups[targetRegion].emails = emails;
        }

        let totalSuccess = 0;
        let totalLogged = 0;

        for (const key of Object.keys(groups)) {
          const grp = groups[key];
          if (grp.emails.length === 0) continue;

          const res = await fetch('/api/admin/send-lead-invitation', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              emails: grp.emails,
              language: grp.lang,
              targetRegion: grp.reg
            })
          });

          const data = await res.json().catch(() => ({}));
          if (res.ok) {
            if (data.success) {
              totalSuccess += (data.successCount || grp.emails.length);
              setSentMap(prev => {
                const next = { ...prev };
                grp.emails.forEach(e => { next[e] = true; });
                return next;
              });
            } else if (data.allLoggedLocally) {
              totalLogged += (data.loggedLocallyCount || grp.emails.length);
              setSentMap(prev => {
                const next = { ...prev };
                grp.emails.forEach(e => { next[e] = true; });
                return next;
              });
            }
          }
        }

        if (totalSuccess > 0) {
          setSendSuccessStatus(
            isUk
              ? `✅ Успішно розіслано пропозиції на мовах регіонів для ${totalSuccess} контактів!`
              : isPl
              ? `✅ Pomyślnie wysłano zaproszenia w językach regionalnych do ${totalSuccess} kontaktów!`
              : `✅ Successfully dispatched regional invitations to ${totalSuccess} contacts!`
          );
        } else if (totalLogged > 0) {
          setErrorMessage(
            isUk
              ? `⚠️ Збережено в базі та зафіксовано в лозі ${totalLogged} листів на мовах відповідних регіонів.`
              : isPl
              ? `⚠️ Zapisano w bazie i zarejestrowano w logach ${totalLogged} wiadomości.`
              : `⚠️ Saved to DB and logged ${totalLogged} localized invitation messages.`
          );
        }
      }
    } catch (err: any) {
      console.error(err);
      setErrorMessage(err.message || 'Error sending invitation.');
    } finally {
      setSendingEmailIndex(null);
      setIsSendingAll(false);
    }
  };

  // Guard for non-admin
  if (!isAdmin) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-8 text-center space-y-4 font-mono shadow-xs">
        <ShieldAlert className="w-12 h-12 text-red-500 mx-auto animate-pulse" />
        <h3 className="text-base font-bold text-red-600 uppercase">Доступ обмежено (Admin Only)</h3>
        <p className="text-xs text-slate-500">
          Цей інструмент доступний виключно для адміністраторів систем екосистеми.
        </p>
        <button
          onClick={onBackToHub}
          className="px-4 py-2 bg-slate-800 text-white font-bold text-xs uppercase rounded cursor-pointer hover:bg-slate-700"
        >
          Повернутися до Хабу
        </button>
      </div>
    );
  }

  const handleHarvestGmail = async () => {
    if (!profession.trim()) {
      setErrorMessage(
        isUk
          ? 'Будь ласка, вкажіть фах або сферу діяльності.'
          : isPl
          ? 'Podaj specjalizację lub branżę do wyszukania.'
          : 'Please enter a profession or industry.'
      );
      return;
    }
    setErrorMessage(null);
    setIsHarvestingGmail(true);
    setGmailResults([]);

    try {
      const res = await fetch('/api/admin/harvest-gmail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          profession,
          region,
          targetRegion,
          customKeywords,
          language: invitationLanguage
        })
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || (isUk ? 'Помилка при виконанні пошуку контактів.' : isPl ? 'Błąd podczas wyszukiwania kontaktów.' : 'Error performing contact search.'));
      }

      const data = await res.json();
      const rawLeads = Array.isArray(data.leads) ? data.leads : [];
      const cleanLeads: HarvestedLead[] = rawLeads.map((item: any) => {
        const assignedReg = (item.region || data.targetRegion || targetRegion) as 'ukraine' | 'poland' | 'eu';
        const assignedLang = (item.language || data.language || (assignedReg === 'poland' ? 'pl' : assignedReg === 'eu' ? 'en' : 'uk')) as 'uk' | 'pl' | 'en';

        if (typeof item === 'string') {
          return {
            email: item.toLowerCase().trim(),
            name_or_title: assignedReg === 'poland' ? `Specjalista (${profession})` : assignedReg === 'eu' ? `Specialist (${profession})` : `Фахівець (${profession})`,
            source_url: 'https://google.com',
            profession: profession,
            city_or_region: region,
            snippet: 'Знайдено в публічних джерелах',
            region: assignedReg,
            language: assignedLang
          };
        }
        if (item && typeof item === 'object') {
          return {
            email: String(item.email || item.contact || item.mail || '').toLowerCase().trim(),
            name_or_title: String(item.name_or_title || item.name || item.title || (assignedReg === 'poland' ? 'Specjalista' : assignedReg === 'eu' ? 'Specialist' : 'Фахівець')).trim(),
            source_url: String(item.source_url || item.url || item.link || 'https://google.com').trim(),
            profession: String(item.profession || profession).trim(),
            city_or_region: String(item.city_or_region || item.city || region).trim(),
            snippet: String(item.snippet || item.description || item.context || 'Публічні контакти').trim(),
            region: assignedReg,
            language: assignedLang
          };
        }
        return null;
      }).filter((item): item is HarvestedLead => item !== null && Boolean(item.email));

      setGmailResults(cleanLeads);
      setGmailSearchSummary(data.summary || (cleanLeads.length > 0 ? `Знайдено ${cleanLeads.length} контактів.` : 'Контактів не знайдено.'));
      setHasSearchedGmail(true);
    } catch (err: any) {
      console.error(err);
      setErrorMessage(err.message || 'Error harvesting leads');
    } finally {
      setIsHarvestingGmail(false);
    }
  };

  const handleHarvestForums = async () => {
    if (!forumTopic.trim()) {
      setErrorMessage(
        isUk
          ? 'Будь ласка, вкажіть тему форуму.'
          : isPl
          ? 'Podaj tematykę forum.'
          : 'Please enter a forum topic.'
      );
      return;
    }
    setErrorMessage(null);
    setIsHarvestingForums(true);
    setForumResults([]);

    try {
      const res = await fetch('/api/admin/harvest-forums', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topic: forumTopic,
          targetRegion
        })
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || (isUk ? 'Помилка при пошуку форумів.' : isPl ? 'Błąd wyszukiwania forów.' : 'Error searching forums.'));
      }

      const data = await res.json();
      const rawForums = Array.isArray(data.forums) ? data.forums : [];
      const cleanForums: ForumLead[] = rawForums.map((item: any) => {
        if (item && typeof item === 'object') {
          return {
            title: String(item.title || item.name || 'Тематичний ресурс').trim(),
            url: String(item.url || item.link || 'https://google.com').trim(),
            topic: String(item.topic || forumTopic).trim(),
            audience_type: String(item.audience_type || item.audience || 'Фахівці').trim(),
            estimated_activity: String(item.estimated_activity || item.activity || 'Активний').trim(),
            description: String(item.description || item.snippet || 'Обговорення та спільноти').trim()
          };
        }
        return null;
      }).filter((item): item is ForumLead => item !== null);

      setForumResults(cleanForums);
    } catch (err: any) {
      console.error(err);
      setErrorMessage(err.message || 'Error harvesting forums');
    } finally {
      setIsHarvestingForums(false);
    }
  };

  const copyToClipboard = (text: string, index?: number) => {
    navigator.clipboard.writeText(text);
    if (index !== undefined) {
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } else {
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2000);
    }
  };

  const downloadGmailCsv = () => {
    if (gmailResults.length === 0) return;
    const header = 'Email,Name/Title,Profession,Region,Source URL,Snippet\n';
    const rows = gmailResults.map(l => 
      `"${l.email}","${(l.name_or_title || '').replace(/"/g, '""')}","${(l.profession || '').replace(/"/g, '""')}","${(l.city_or_region || '').replace(/"/g, '""')}","${l.source_url}","${(l.snippet || '').replace(/"/g, '""')}"`
    ).join('\n');

    const blob = new Blob(['\uFEFF' + header + rows], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `leads_${targetRegion}_${profession.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadForumsCsv = () => {
    if (forumResults.length === 0) return;
    const header = 'Title,URL,Topic,Audience,Activity,Description\n';
    const rows = forumResults.map(f => 
      `"${(f.title || '').replace(/"/g, '""')}","${f.url}","${(f.topic || '').replace(/"/g, '""')}","${(f.audience_type || '').replace(/"/g, '""')}","${(f.estimated_activity || '').replace(/"/g, '""')}","${(f.description || '').replace(/"/g, '""')}"`
    ).join('\n');

    const blob = new Blob(['\uFEFF' + header + rows], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `forums_${targetRegion}_${forumTopic.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6 animate-fade-in font-mono">
      
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-red-950 via-slate-900 to-slate-950 text-white rounded-xl p-6 border border-red-900/40 shadow-lg">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 text-[9px] font-bold uppercase rounded bg-red-600/30 border border-red-500/50 text-red-300">
                ADMIN EXCLUSIVE OSINT LEAD HARVESTER (UA / PL / EU)
              </span>
            </div>
            <h1 className="text-xl font-bold uppercase tracking-tight flex items-center gap-2 text-white">
              <UserCheck className="w-6 h-6 text-red-400" />
              <span>
                {isPl
                  ? 'Parser Kontaktów Email & Forów (Polska / UE / Ukraina)'
                  : isEn
                  ? 'B2B Lead & Email Harvester (Ukraine / Poland / EU)'
                  : 'Парсер Контактів Email & Форумів (Україна / Польща / ЄС)'}
              </span>
            </h1>
            <p className="text-xs text-slate-300 max-w-2xl leading-relaxed">
              {isPl
                ? 'Automatyczne wyszukiwanie kontaktów biznesowych (adresów email firm, biur i specjalistów) oraz forów w Polsce, Unii Europejskiej i Ukrainie z wysyłką zaproszeń w języku polskim, angielskim i ukraińskim.'
                : isEn
                ? 'Automated search for business contacts (enterprise emails, consultancies, freelancers) and industry forums across Poland, European Union, and Ukraine with localized multilingual invitations.'
                : 'Автоматизований пошук контактів (email-адрес фахівців, компаній, бюро) та форумів в Україні, Польщі та країнах ЄС із нативною відправкою персоналізованих запрошень трьома мовами.'}
            </p>
          </div>

          <button
            onClick={onBackToHub}
            className="self-start sm:self-center px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs uppercase rounded-lg border border-slate-700 transition-all flex items-center gap-1.5 cursor-pointer shrink-0"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{isPl ? 'Do Hubu' : isEn ? 'Back to Hub' : 'До Хабу'}</span>
          </button>
        </div>

        {/* Target Region & Market Navigation Pills */}
        <div className="mt-5 p-3.5 bg-slate-950/80 border border-slate-800 rounded-xl flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-emerald-400 shrink-0" />
            <span className="text-xs font-bold uppercase tracking-wider text-slate-300">
              {isPl ? 'Rynek Docelowy / Gdzie szukać:' : isEn ? 'Target Geography / Where to search:' : 'Цільовий Ринок / Де шукати контакти:'}
            </span>
          </div>

          {/* High-Contrast Region Buttons */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleSelectTargetRegion('ukraine')}
              className={`px-3.5 py-1.5 text-xs font-bold rounded-lg border transition-all flex items-center gap-1.5 cursor-pointer ${
                targetRegion === 'ukraine'
                  ? 'bg-blue-600 border-blue-400 text-white shadow-md ring-2 ring-blue-500/40'
                  : 'bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <span className="text-sm">🇺🇦</span>
              <span>Україна (Ukraine)</span>
            </button>

            <button
              onClick={() => handleSelectTargetRegion('poland')}
              className={`px-3.5 py-1.5 text-xs font-bold rounded-lg border transition-all flex items-center gap-1.5 cursor-pointer ${
                targetRegion === 'poland'
                  ? 'bg-rose-600 border-rose-400 text-white shadow-md ring-2 ring-rose-500/40'
                  : 'bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <span className="text-sm">🇵🇱</span>
              <span>Польща (Polska / Poland)</span>
            </button>

            <button
              onClick={() => handleSelectTargetRegion('eu')}
              className={`px-3.5 py-1.5 text-xs font-bold rounded-lg border transition-all flex items-center gap-1.5 cursor-pointer ${
                targetRegion === 'eu'
                  ? 'bg-amber-600 border-amber-400 text-white shadow-md ring-2 ring-amber-500/40'
                  : 'bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <span className="text-sm">🇪🇺</span>
              <span>Європейський Союз (EU)</span>
            </button>
          </div>

          {/* Invitation Language Override */}
          <div className="flex items-center gap-2 border-t md:border-t-0 md:border-l border-slate-800 pt-2 md:pt-0 md:pl-3">
            <Languages className="w-4 h-4 text-cyan-400 shrink-0" />
            <span className="text-[11px] font-bold text-slate-400 uppercase">
              {isPl ? 'Język zaproszenia:' : isEn ? 'Email Language:' : 'Мова листа:'}
            </span>
            <div className="flex gap-1 bg-slate-900 p-0.5 rounded-lg border border-slate-700">
              <button
                type="button"
                onClick={() => setInvitationLanguage('uk')}
                className={`px-2 py-0.5 text-[10px] font-bold rounded transition-colors cursor-pointer ${
                  invitationLanguage === 'uk' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'
                }`}
                title="Українська мова листа"
              >
                🇺🇦 UA
              </button>
              <button
                type="button"
                onClick={() => setInvitationLanguage('pl')}
                className={`px-2 py-0.5 text-[10px] font-bold rounded transition-colors cursor-pointer ${
                  invitationLanguage === 'pl' ? 'bg-rose-600 text-white' : 'text-slate-400 hover:text-white'
                }`}
                title="Język polski"
              >
                🇵🇱 PL
              </button>
              <button
                type="button"
                onClick={() => setInvitationLanguage('en')}
                className={`px-2 py-0.5 text-[10px] font-bold rounded transition-colors cursor-pointer ${
                  invitationLanguage === 'en' ? 'bg-amber-600 text-white' : 'text-slate-400 hover:text-white'
                }`}
                title="English language"
              >
                🇬🇧 EN
              </button>
            </div>
          </div>
        </div>

        {/* Autopilot Master Action Block with Multi-Sector & Profession Selector */}
        <div className="mt-4 p-4 rounded-xl bg-gradient-to-r from-red-950/90 via-slate-900 to-rose-950/90 border border-red-800/60 shadow-lg space-y-4">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="space-y-1.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-red-600 text-white shadow-xs animate-pulse flex items-center gap-1">
                  <Sparkles className="w-3 h-3 text-amber-300" />
                  <span>AUTOPILOT 2026</span>
                </span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-800 border border-slate-700 text-amber-300 flex items-center gap-1">
                  <Briefcase className="w-3 h-3" />
                  <span>
                    {isPl
                      ? `Wybrano: ${selectedAutopilotRoles.length} z ${getAllAutopilotRolesForRegion(targetRegion).length} zawodów`
                      : isEn
                      ? `Selected: ${selectedAutopilotRoles.length} of ${getAllAutopilotRolesForRegion(targetRegion).length} professions`
                      : `Обрано: ${selectedAutopilotRoles.length} з ${getAllAutopilotRolesForRegion(targetRegion).length} професій`}
                  </span>
                </span>
                <span className="text-xs font-bold text-white flex items-center gap-1">
                  <span>
                    {targetRegion === 'poland' ? '🇵🇱 Polska' : targetRegion === 'eu' ? '🇪🇺 European Union' : '🇺🇦 Україна'}
                  </span>
                </span>
              </div>
              <p className="text-[11px] text-slate-300 leading-relaxed max-w-3xl">
                {isPl
                  ? `Автономний автопілот почергово сканує публічні пошти за всіма обраними професіями (юристи, нотаріуси, викладачі/професори, бухгалтери, кошторисники, митні брокери, фармацевти, агрономи, CISO, тендерні спеціалісти), розсилає персоналізовані листи мовою [${invitationLanguage.toUpperCase()}] та формує детальний Executive Report.`
                  : isEn
                  ? `Autonomously harvests public emails across all selected professions (lawyers, notaries, university professors, accountants, quantity surveyors, customs brokers, pharma specialists, agronomists, CISO, tender officers), dispatches personalized invitations in [${invitationLanguage.toUpperCase()}], and outputs a structured breakdown report.`
                  : `Автономний автопілот почергово сканує публічні пошти за всіма обраними професіями (юристи, нотаріуси, викладачі/професори, бухгалтери, кошторисники, митні брокери, фармацевти, агрономи, CISO, тендерні спеціалісти), розсилає персоналізовані листи мовою [${invitationLanguage.toUpperCase()}] та формує детальний Executive Report.`}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2 shrink-0 w-full md:w-auto">
              <button
                type="button"
                onClick={() => setShowProfessionDrawer(!showProfessionDrawer)}
                className="px-3.5 py-2.5 bg-slate-800/90 hover:bg-slate-700 border border-slate-600/80 text-white font-bold text-xs rounded-xl shadow transition-all flex items-center gap-1.5 cursor-pointer"
              >
                <Sliders className="w-3.5 h-3.5 text-amber-400" />
                <span>
                  {isPl
                    ? `Zarządzaj zawodami (${selectedAutopilotRoles.length})`
                    : isEn
                    ? `Manage Professions (${selectedAutopilotRoles.length})`
                    : `Налаштувати професії (${selectedAutopilotRoles.length})`}
                </span>
                {showProfessionDrawer ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
              </button>

              <button
                type="button"
                onClick={handleRunAutopilot}
                disabled={isAutopilotRunning || selectedAutopilotRoles.length === 0}
                className="flex-1 md:flex-none px-5 py-2.5 bg-gradient-to-r from-red-600 via-rose-600 to-amber-600 hover:from-red-500 hover:to-amber-500 disabled:opacity-50 text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-lg hover:shadow-red-500/30 transition-all flex items-center justify-center gap-2 cursor-pointer border border-red-400/40 transform active:scale-98"
              >
                {isAutopilotRunning ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin text-white" />
                    <span>
                      {isPl
                        ? `Praca w toku (${autopilotCurrentSectorIndex}/${autopilotTotalSectors})...`
                        : isEn
                        ? `Running (${autopilotCurrentSectorIndex}/${autopilotTotalSectors})...`
                        : `Обробка (${autopilotCurrentSectorIndex}/${autopilotTotalSectors})...`}
                    </span>
                  </>
                ) : (
                  <>
                    <Rocket className="w-4 h-4 text-amber-300 animate-bounce" />
                    <span>
                      {isPl
                        ? `🚀 Uruchom (${selectedAutopilotRoles.length}) [${invitationLanguage.toUpperCase()}]`
                        : isEn
                        ? `🚀 Launch (${selectedAutopilotRoles.length}) [${invitationLanguage.toUpperCase()}]`
                        : `🚀 Запустити Автопілот (${selectedAutopilotRoles.length} фахів) [${invitationLanguage.toUpperCase()}]`}
                    </span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Quick Category Chips */}
          <div className="pt-2 border-t border-red-900/40 flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-bold text-slate-400 mr-1 flex items-center gap-1">
              <Sparkles className="w-3 h-3 text-amber-400" />
              <span>{isUk ? 'Швидкі пресети:' : isPl ? 'Szybkie presety:' : 'Quick presets:'}</span>
            </span>

            <button
              type="button"
              onClick={() => setSelectedAutopilotRoles(getAllAutopilotRolesForRegion(targetRegion))}
              className="px-2.5 py-1 text-[10px] font-black rounded-lg bg-emerald-950/80 border border-emerald-600/60 text-emerald-300 hover:bg-emerald-900 transition-all cursor-pointer flex items-center gap-1"
            >
              <Check className="w-3 h-3" />
              <span>{isUk ? `Всі 11 сфер (${getAllAutopilotRolesForRegion(targetRegion).length})` : isPl ? `Wszystkie (${getAllAutopilotRolesForRegion(targetRegion).length})` : `All 11 Sectors (${getAllAutopilotRolesForRegion(targetRegion).length})`}</span>
            </button>

            <button
              type="button"
              onClick={() => {
                const legalGroup = AUTOPILOT_PROFESSION_GROUPS.find(g => g.id === 'legal_notary');
                if (legalGroup) {
                  const roles = targetRegion === 'poland' ? legalGroup.rolesPl : targetRegion === 'eu' ? legalGroup.rolesEn : legalGroup.rolesUk;
                  setSelectedAutopilotRoles(roles);
                }
              }}
              className="px-2.5 py-1 text-[10px] font-bold rounded-lg bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700 hover:text-white transition-all cursor-pointer flex items-center gap-1"
            >
              <Scale className="w-3 h-3 text-indigo-400" />
              <span>{isUk ? '⚖️ Юристи & Нотаріуси' : isPl ? '⚖️ Prawnicy & Notariusze' : '⚖️ Lawyers & Notaries'}</span>
            </button>

            <button
              type="button"
              onClick={() => {
                const scienceGroup = AUTOPILOT_PROFESSION_GROUPS.find(g => g.id === 'science_academia');
                if (scienceGroup) {
                  const roles = targetRegion === 'poland' ? scienceGroup.rolesPl : targetRegion === 'eu' ? scienceGroup.rolesEn : scienceGroup.rolesUk;
                  setSelectedAutopilotRoles(roles);
                }
              }}
              className="px-2.5 py-1 text-[10px] font-bold rounded-lg bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700 hover:text-white transition-all cursor-pointer flex items-center gap-1"
            >
              <GraduationCap className="w-3 h-3 text-cyan-400" />
              <span>{isUk ? '🎓 Наука, ВНЗ & Професори' : isPl ? '🎓 Nauka, Uczelnie & Profesorowie' : '🎓 Science & Academia'}</span>
            </button>

            <button
              type="button"
              onClick={() => {
                const taxGroup = AUTOPILOT_PROFESSION_GROUPS.find(g => g.id === 'tax_accounting');
                if (taxGroup) {
                  const roles = targetRegion === 'poland' ? taxGroup.rolesPl : targetRegion === 'eu' ? taxGroup.rolesEn : taxGroup.rolesUk;
                  setSelectedAutopilotRoles(roles);
                }
              }}
              className="px-2.5 py-1 text-[10px] font-bold rounded-lg bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700 hover:text-white transition-all cursor-pointer flex items-center gap-1"
            >
              <Briefcase className="w-3 h-3 text-emerald-400" />
              <span>{isUk ? '🏛️ Бухгалтери & Податки' : isPl ? '🏛️ Księgowi & Podatki' : '🏛️ Accountants & Tax'}</span>
            </button>

            <button
              type="button"
              onClick={() => {
                const constrGroup = AUTOPILOT_PROFESSION_GROUPS.find(g => g.id === 'construction_bim');
                if (constrGroup) {
                  const roles = targetRegion === 'poland' ? constrGroup.rolesPl : targetRegion === 'eu' ? constrGroup.rolesEn : constrGroup.rolesUk;
                  setSelectedAutopilotRoles(roles);
                }
              }}
              className="px-2.5 py-1 text-[10px] font-bold rounded-lg bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700 hover:text-white transition-all cursor-pointer flex items-center gap-1"
            >
              <HardHat className="w-3 h-3 text-amber-400" />
              <span>{isUk ? '🏗️ Будівництво & Кошториси' : isPl ? '🏗️ Budownictwo & Kosztorysy' : '🏗️ Construction & Costing'}</span>
            </button>

            <button
              type="button"
              onClick={() => {
                const logGroup = AUTOPILOT_PROFESSION_GROUPS.find(g => g.id === 'logistics_customs');
                if (logGroup) {
                  const roles = targetRegion === 'poland' ? logGroup.rolesPl : targetRegion === 'eu' ? logGroup.rolesEn : logGroup.rolesUk;
                  setSelectedAutopilotRoles(roles);
                }
              }}
              className="px-2.5 py-1 text-[10px] font-bold rounded-lg bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700 hover:text-white transition-all cursor-pointer flex items-center gap-1"
            >
              <Truck className="w-3 h-3 text-blue-400" />
              <span>{isUk ? '🚢 Логістика & ЗЕД' : isPl ? '🚢 Logistyka & Cło' : '🚢 Logistics & Customs'}</span>
            </button>

            <button
              type="button"
              onClick={() => {
                const pharmaGroup = AUTOPILOT_PROFESSION_GROUPS.find(g => g.id === 'pharma_healthcare');
                if (pharmaGroup) {
                  const roles = targetRegion === 'poland' ? pharmaGroup.rolesPl : targetRegion === 'eu' ? pharmaGroup.rolesEn : pharmaGroup.rolesUk;
                  setSelectedAutopilotRoles(roles);
                }
              }}
              className="px-2.5 py-1 text-[10px] font-bold rounded-lg bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700 hover:text-white transition-all cursor-pointer flex items-center gap-1"
            >
              <Stethoscope className="w-3 h-3 text-rose-400" />
              <span>{isUk ? '💊 Фармація & Аптеки' : isPl ? '💊 Farmacja & Apteki' : '💊 Pharma & Healthcare'}</span>
            </button>

            <button
              type="button"
              onClick={() => {
                const agriGroup = AUTOPILOT_PROFESSION_GROUPS.find(g => g.id === 'agri_cadastre');
                if (agriGroup) {
                  const roles = targetRegion === 'poland' ? agriGroup.rolesPl : targetRegion === 'eu' ? agriGroup.rolesEn : agriGroup.rolesUk;
                  setSelectedAutopilotRoles(roles);
                }
              }}
              className="px-2.5 py-1 text-[10px] font-bold rounded-lg bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700 hover:text-white transition-all cursor-pointer flex items-center gap-1"
            >
              <Wheat className="w-3 h-3 text-yellow-400" />
              <span>{isUk ? '🚜 Агро & Кадастр' : isPl ? '🚜 Rolnictwo & Kataster' : '🚜 Agri & Land'}</span>
            </button>

            <button
              type="button"
              onClick={() => {
                const energyGroup = AUTOPILOT_PROFESSION_GROUPS.find(g => g.id === 'energy_esg');
                if (energyGroup) {
                  const roles = targetRegion === 'poland' ? energyGroup.rolesPl : targetRegion === 'eu' ? energyGroup.rolesEn : energyGroup.rolesUk;
                  setSelectedAutopilotRoles(roles);
                }
              }}
              className="px-2.5 py-1 text-[10px] font-bold rounded-lg bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700 hover:text-white transition-all cursor-pointer flex items-center gap-1"
            >
              <Zap className="w-3 h-3 text-amber-300" />
              <span>{isUk ? '⚡ Енергетика & CBAM' : isPl ? '⚡ Energetyka & CBAM' : '⚡ Energy & ESG'}</span>
            </button>

            <button
              type="button"
              onClick={() => {
                const cyberGroup = AUTOPILOT_PROFESSION_GROUPS.find(g => g.id === 'cyber_it');
                if (cyberGroup) {
                  const roles = targetRegion === 'poland' ? cyberGroup.rolesPl : targetRegion === 'eu' ? cyberGroup.rolesEn : cyberGroup.rolesUk;
                  setSelectedAutopilotRoles(roles);
                }
              }}
              className="px-2.5 py-1 text-[10px] font-bold rounded-lg bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700 hover:text-white transition-all cursor-pointer flex items-center gap-1"
            >
              <ShieldCheck className="w-3 h-3 text-red-400" />
              <span>{isUk ? '🔒 Кібербезпека & IT' : isPl ? '🔒 Cyberbezpieczeństwo' : '🔒 Cybersecurity & IT'}</span>
            </button>

            <button
              type="button"
              onClick={() => setSelectedAutopilotRoles([])}
              className="px-2.5 py-1 text-[10px] font-bold rounded-lg bg-red-950/60 border border-red-800/60 text-red-300 hover:bg-red-900 transition-all cursor-pointer ml-auto flex items-center gap-1"
            >
              <X className="w-3 h-3" />
              <span>{isUk ? 'Зняти вибір' : isPl ? 'Wyczyść' : 'Deselect All'}</span>
            </button>
          </div>

          {/* Interactive Expandable Professions Checklist Drawer */}
          {showProfessionDrawer && (
            <div className="mt-3 p-4 rounded-xl bg-slate-950/90 border border-slate-800 shadow-2xl space-y-4 animate-in fade-in duration-200">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pb-3 border-b border-slate-800">
                <div className="flex items-center gap-2">
                  <Sliders className="w-4 h-4 text-red-400" />
                  <h4 className="text-xs font-bold text-white uppercase tracking-wider">
                    {isUk ? 'Вибір професій та галузей для автоматичного сканування' : isPl ? 'Wybór zawodów i branż do automatycznego skanowania' : 'Select Professions & Sectors for Autopilot'}
                  </h4>
                </div>

                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <div className="relative flex-1 sm:w-64">
                    <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={professionSearchFilter}
                      onChange={(e) => setProfessionSearchFilter(e.target.value)}
                      placeholder={isUk ? 'Фільтр професій...' : isPl ? 'Filtruj zawody...' : 'Filter professions...'}
                      className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-hidden focus:border-red-500"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedAutopilotRoles(getAllAutopilotRolesForRegion(targetRegion))}
                    className="px-2.5 py-1.5 text-[11px] font-bold bg-slate-800 hover:bg-slate-700 text-white rounded-lg transition-colors cursor-pointer"
                  >
                    {isUk ? 'Всі' : isPl ? 'Wszystkie' : 'All'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedAutopilotRoles([])}
                    className="px-2.5 py-1.5 text-[11px] font-bold bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white rounded-lg transition-colors cursor-pointer"
                  >
                    {isUk ? 'Очистити' : isPl ? 'Wyczyść' : 'Clear'}
                  </button>
                </div>
              </div>

              {/* Grid of Categories */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 max-h-96 overflow-y-auto pr-1">
                {AUTOPILOT_PROFESSION_GROUPS.map((group) => {
                  const groupRoles = targetRegion === 'poland' ? group.rolesPl : targetRegion === 'eu' ? group.rolesEn : group.rolesUk;
                  const groupTitle = targetRegion === 'poland' ? group.titlePl : targetRegion === 'eu' ? group.titleEn : group.titleUk;
                  
                  // Filter roles if search query is provided
                  const filteredRoles = professionSearchFilter.trim()
                    ? groupRoles.filter(r => r.toLowerCase().includes(professionSearchFilter.toLowerCase()))
                    : groupRoles;

                  if (filteredRoles.length === 0) return null;

                  const selectedInGroup = groupRoles.filter(r => selectedAutopilotRoles.includes(r));
                  const isAllGroupSelected = groupRoles.length > 0 && selectedInGroup.length === groupRoles.length;
                  const isSomeGroupSelected = selectedInGroup.length > 0 && !isAllGroupSelected;

                  const handleToggleEntireGroup = () => {
                    if (isAllGroupSelected) {
                      // Deselect group roles
                      setSelectedAutopilotRoles(prev => prev.filter(r => !groupRoles.includes(r)));
                    } else {
                      // Select all group roles
                      setSelectedAutopilotRoles(prev => Array.from(new Set([...prev, ...groupRoles])));
                    }
                  };

                  return (
                    <div key={group.id} className="p-3 rounded-xl bg-slate-900/80 border border-slate-800 hover:border-slate-700 transition-all space-y-2">
                      <div className="flex items-center justify-between gap-2 pb-1.5 border-b border-slate-800/80">
                        <button
                          type="button"
                          onClick={handleToggleEntireGroup}
                          className="flex items-center gap-2 text-left cursor-pointer group flex-1"
                        >
                          <div className="w-4 h-4 flex items-center justify-center text-slate-400 group-hover:text-red-400 transition-colors">
                            {isAllGroupSelected ? (
                              <CheckSquare className="w-4 h-4 text-red-500" />
                            ) : isSomeGroupSelected ? (
                              <div className="w-3.5 h-3.5 rounded-xs bg-red-600 flex items-center justify-center text-white text-[9px] font-black leading-none">−</div>
                            ) : (
                              <Square className="w-4 h-4 text-slate-600" />
                            )}
                          </div>
                          <span className="text-xs font-bold text-slate-200 group-hover:text-white transition-colors line-clamp-1">
                            {groupTitle}
                          </span>
                        </button>
                        <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-slate-800 text-slate-400">
                          {selectedInGroup.length}/{groupRoles.length}
                        </span>
                      </div>

                      <div className="space-y-1 pl-1">
                        {filteredRoles.map((role) => {
                          const isSelected = selectedAutopilotRoles.includes(role);
                          return (
                            <label
                              key={role}
                              className={`flex items-start gap-2 p-1.5 rounded-lg text-[11px] cursor-pointer transition-colors ${
                                isSelected
                                  ? 'bg-red-950/40 text-slate-100 font-medium'
                                  : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-200'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedAutopilotRoles(prev => [...prev, role]);
                                  } else {
                                    setSelectedAutopilotRoles(prev => prev.filter(r => r !== role));
                                  }
                                }}
                                className="mt-0.5 w-3.5 h-3.5 rounded-xs border-slate-700 bg-slate-900 text-red-600 focus:ring-red-500 cursor-pointer"
                              />
                              <span className="leading-snug">{role}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Sub-Tabs */}
        <div className="flex gap-2 mt-4 border-t border-slate-800 pt-4">
          <button
            onClick={() => setActiveTab('gmail')}
            className={`px-4 py-2 text-xs font-bold uppercase rounded-lg transition-all flex items-center gap-2 cursor-pointer ${
              activeTab === 'gmail' 
                ? 'bg-red-600 text-white shadow' 
                : 'bg-slate-800/60 text-slate-400 hover:bg-slate-800 hover:text-white'
            }`}
          >
            <Mail className="w-4 h-4" />
            <span>{isPl ? 'Wyszukiwanie Email' : isEn ? 'Search Emails' : 'Пошук Email-Пошт'}</span>
            {gmailResults.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-white/20 rounded-full">{gmailResults.length}</span>
            )}
          </button>

          <button
            onClick={() => setActiveTab('forums')}
            className={`px-4 py-2 text-xs font-bold uppercase rounded-lg transition-all flex items-center gap-2 cursor-pointer ${
              activeTab === 'forums' 
                ? 'bg-red-600 text-white shadow' 
                : 'bg-slate-800/60 text-slate-400 hover:bg-slate-800 hover:text-white'
            }`}
          >
            <Globe className="w-4 h-4" />
            <span>{isPl ? 'Wyszukiwanie Forów' : isEn ? 'Search Forums' : 'Пошук Форумів & Спільнот'}</span>
            {forumResults.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-white/20 rounded-full">{forumResults.length}</span>
            )}
          </button>

          <button
            onClick={() => setActiveTab('import')}
            className={`px-4 py-2 text-xs font-bold uppercase rounded-lg transition-all flex items-center gap-2 cursor-pointer ${
              activeTab === 'import' 
                ? 'bg-red-600 text-white shadow' 
                : 'bg-slate-800/60 text-slate-400 hover:bg-slate-800 hover:text-white'
            }`}
          >
            <Upload className="w-4 h-4" />
            <span>📥 {isPl ? 'Import Masowy & Wysyłka' : isEn ? 'Mass Import & Broadcast' : 'Масовий Імпорт & Розсилка'}</span>
          </button>
        </div>
      </div>

      {sendSuccessStatus && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-800 text-xs flex items-center gap-2 shadow-xs">
          <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600" />
          <span className="font-semibold">{sendSuccessStatus}</span>
        </div>
      )}

      {errorMessage && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs flex items-center gap-2 shadow-xs">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span className="font-semibold">{errorMessage}</span>
        </div>
      )}

      {/* TAB 1: GMAIL SEARCH */}
      {activeTab === 'gmail' && (
        <div className="space-y-6">
          
          {/* Quick Direct Single Email Invitation Block */}
          <div className="bg-gradient-to-r from-slate-900 via-slate-950 to-slate-900 border border-slate-800 rounded-xl p-5 text-white shadow-md space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 border-b border-slate-800 pb-2.5">
              <h3 className="text-xs font-bold uppercase tracking-wider text-red-400 flex items-center gap-2">
                <Send className="w-4 h-4 text-red-500" />
                <span>
                  {isPl
                    ? 'Bezpośrednia wysyłka zaproszenia na konkretny adres e-mail'
                    : isEn
                    ? 'Direct single email invitation send'
                    : 'Точкова відправка запрошення на конкретну пошту'}
                </span>
              </h3>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-slate-300 bg-slate-800 px-2 py-0.5 rounded border border-slate-700 font-mono">
                  {invitationLanguage === 'pl' ? 'Лист: 🇵🇱 Polski' : invitationLanguage === 'en' ? 'Лист: 🇬🇧 English' : 'Лист: 🇺🇦 UA'}
                </span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 items-center">
              <div className="relative flex-1 w-full">
                <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="email"
                  value={customEmailInput}
                  onChange={(e) => setCustomEmailInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && customEmailInput.trim()) {
                      e.preventDefault();
                      handleSendCustomEmail();
                    }
                  }}
                  placeholder={
                    isPl
                      ? 'Wpisz adres email do wysłania zaproszenia (np. kontakt@biuro-rachunkowe.pl)...'
                      : isEn
                      ? 'Enter target email address to send invitation (e.g. contact@company.eu)...'
                      : 'Введіть конкретну email адресу для надсилання запрошення (напр. user@gmail.com)...'
                  }
                  className="w-full bg-slate-950 border border-slate-700/80 rounded-lg pl-9 pr-3 py-2.5 text-xs font-mono text-white placeholder-slate-500 focus:outline-none focus:border-red-500 transition-colors"
                />
              </div>

              <button
                onClick={handleSendCustomEmail}
                disabled={isSendingCustom || !customEmailInput.trim()}
                className="w-full sm:w-auto px-5 py-2.5 bg-red-600 hover:bg-red-500 disabled:bg-slate-800 disabled:text-slate-500 text-white font-bold text-xs uppercase rounded-lg shadow-sm transition-all flex items-center justify-center gap-2 shrink-0 cursor-pointer"
              >
                {isSendingCustom ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>{isPl ? 'Wysyłanie...' : isEn ? 'Sending...' : 'Надсилання...'}</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>{isPl ? 'Wyślij zaproszenie' : isEn ? 'Send Invitation' : 'Надіслати запрошення'}</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Controls Form */}
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-xs space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-3">
              <h3 className="text-xs font-bold uppercase text-slate-800 tracking-wider flex items-center gap-2">
                <Filter className="w-4 h-4 text-red-600" />
                <span>
                  {isPl
                    ? 'Parametry wyszukiwania kontaktów biznesowych'
                    : isEn
                    ? 'Lead Intelligence Search Parameters'
                    : 'Параметри розвідки контактів'}
                </span>
              </h3>

              {/* Target Region Indicator Badge */}
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] uppercase font-bold text-slate-400">
                  {isPl ? 'Wybrany rynek:' : isEn ? 'Selected Market:' : 'Обраний ринок:'}
                </span>
                <span className="px-2.5 py-0.5 rounded text-[11px] font-bold bg-slate-100 border border-slate-300 text-slate-800 flex items-center gap-1">
                  <span>{targetRegion === 'poland' ? '🇵🇱 Polska' : targetRegion === 'eu' ? '🇪🇺 European Union' : '🇺🇦 Україна'}</span>
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              
              {/* Profession Input with Agents Dropdown & Datalist */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold uppercase text-slate-500 flex items-center justify-between">
                  <span>{isPl ? 'Kryterium / Branża / Moduł' : isEn ? 'Profession / Sector / Module' : 'Критерій / Фах / Сфера'}</span>
                  <span className="text-[9px] text-red-600 font-bold bg-red-50 border border-red-200 px-1.5 py-0.5 rounded flex items-center gap-1">
                    <Briefcase className="w-2.5 h-2.5" />
                    <span>{allPlatformAgentRoles.length} {isPl ? 'agentów' : isEn ? 'agents' : 'агентів'}</span>
                  </span>
                </label>

                {/* Dropdown list of all platform agents grouped by operational hub / category */}
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      setProfession(e.target.value);
                    }
                  }}
                  className="w-full bg-slate-50 hover:bg-white border border-slate-300 hover:border-red-400 rounded px-2.5 py-1.5 text-xs font-semibold text-slate-800 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 cursor-pointer shadow-2xs transition-colors"
                  value=""
                >
                  <option value="" disabled>
                    {isPl
                      ? '▼ Wybierz profil z bazy agentów platformy (100+)...'
                      : isEn
                      ? '▼ Select profile from Platform Agents (100+)...'
                      : '▼ Оберіть фах з бази агентів платформи (100+)...'}
                  </option>
                  {Object.entries(platformAgentCategories).map(([categoryName, agents]) => (
                    <optgroup key={categoryName} label={`📂 ${categoryName}`}>
                      {agents.map((ag) => (
                        <option key={ag.id} value={ag.targetRole}>
                          {ag.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>

                {/* Freeform text input for typing or custom refinement */}
                <input
                  type="text"
                  value={profession}
                  onChange={(e) => setProfession(e.target.value)}
                  list="profession-options"
                  placeholder={
                    targetRegion === 'poland'
                      ? 'np. biuro rachunkowe, radca prawny, kierownik budowy...'
                      : targetRegion === 'eu'
                      ? 'e.g. corporate accountant, customs broker, fidic engineer...'
                      : 'напр. бухгалтер, юрист, кошторисник, логіст...'
                  }
                  className="w-full bg-white border border-slate-200 rounded px-3 py-2 text-xs font-mono text-slate-800 focus:outline-none focus:border-red-500 shadow-2xs"
                />
                <datalist id="profession-options">
                  {allPlatformAgentRoles.map((p, i) => (
                    <option key={i} value={p} />
                  ))}
                </datalist>
              </div>

              {/* Region / City Dropdown/Input */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold uppercase text-slate-500 flex items-center justify-between">
                  <span>{isPl ? 'Region / Kraj / Miasto' : isEn ? 'Region / Country / City' : 'Регіон / Країна / Місто'}</span>
                </label>
                <input
                  type="text"
                  value={region}
                  onChange={(e) => setRegion(e.target.value)}
                  list="region-options"
                  placeholder={
                    targetRegion === 'poland'
                      ? 'Polska, Warszawa, Kraków, Wrocław, Gdańsk, Poznań...'
                      : targetRegion === 'eu'
                      ? 'European Union, Germany, France, Poland, Spain, Italy...'
                      : 'Україна, Київ, Львів, Дніпро, Одеса, Харків...'
                  }
                  className="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2 text-xs font-mono text-slate-800 focus:outline-none focus:border-red-500"
                />
                <datalist id="region-options">
                  {targetRegion === 'poland' && (
                    <>
                      <option value="Polska" />
                      <option value="Warszawa" />
                      <option value="Kraków" />
                      <option value="Wrocław" />
                      <option value="Gdańsk / Trójmiasto" />
                      <option value="Poznań" />
                      <option value="Katowice / Śląsk" />
                    </>
                  )}
                  {targetRegion === 'eu' && (
                    <>
                      <option value="European Union / EU" />
                      <option value="Germany (Berlin, Munich, Frankfurt)" />
                      <option value="France (Paris, Lyon)" />
                      <option value="Poland (Warsaw, Krakow)" />
                      <option value="Netherlands (Amsterdam, Rotterdam)" />
                      <option value="Spain (Madrid, Barcelona)" />
                      <option value="Italy (Milan, Rome)" />
                      <option value="Baltic States (Estonia, Latvia, Lithuania)" />
                    </>
                  )}
                  {targetRegion === 'ukraine' && (
                    <>
                      <option value="Україна" />
                      <option value="Київ" />
                      <option value="Львів" />
                      <option value="Дніпро" />
                      <option value="Одеса" />
                      <option value="Харків" />
                    </>
                  )}
                </datalist>
              </div>

              {/* Custom keywords */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold uppercase text-slate-500">
                  {isPl ? 'Dodatkowe słowa kluczowe (opcjonalnie)' : isEn ? 'Extra Keywords (Optional)' : 'Дод. Ключові слова (необов\'язково)'}
                </label>
                <input
                  type="text"
                  value={customKeywords}
                  onChange={(e) => setCustomKeywords(e.target.value)}
                  placeholder={
                    targetRegion === 'poland'
                      ? 'np. usługi, KSeF, kontakt, cennik, B2B...'
                      : targetRegion === 'eu'
                      ? 'e.g. consulting, VAT, export, contact, RFP...'
                      : 'напр. послуги, резюме, ФОП, контакт...'
                  }
                  className="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2 text-xs font-mono text-slate-800 focus:outline-none focus:border-red-500"
                />
              </div>
            </div>

            {/* Quick Preset Buttons */}
            <div className="space-y-1.5 pt-1">
              <label className="text-[10px] font-bold uppercase text-slate-400">
                {isPl ? '💡 Szybkie szablony branżowe dla wybranego rynku:' : isEn ? '💡 Quick sector templates for selected geography:' : '💡 Швидкі галузеві пресети для обраного ринку:'}
              </label>
              <div className="flex flex-wrap gap-1.5">
                {professionPresets[targetRegion].map((preset, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setProfession(preset)}
                    className="px-2.5 py-1 text-[11px] bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md border border-slate-200 transition-colors cursor-pointer"
                  >
                    + {preset}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3 pt-2 border-t border-slate-100">
              <div className="text-[10px] text-slate-400">
                {targetRegion === 'poland'
                  ? '* Wyszukuje otwarte kontakty (@gmail.com, @wp.pl, @onet.pl, domeny firmowe) w polskim Internecie w czasie rzeczywistym'
                  : targetRegion === 'eu'
                  ? '* Searches verified public emails across European business directories and websites via Google OSINT'
                  : '* Шукає публічно розміщені пошти @gmail.com та корпоративні контакти через веб-пошук Google в реальному часі'}
              </div>

              <button
                onClick={handleHarvestGmail}
                disabled={isHarvestingGmail}
                className="px-6 py-2.5 bg-red-600 hover:bg-red-700 disabled:bg-slate-300 text-white font-bold text-xs uppercase rounded-lg shadow-sm transition-all flex items-center justify-center gap-2 cursor-pointer shrink-0"
              >
                {isHarvestingGmail ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>{isPl ? 'Skanowanie sieci...' : isEn ? 'Scanning Web...' : 'Сканування мережі...'}</span>
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4" />
                    <span>{isPl ? 'Szukaj kontaktów e-mail' : isEn ? 'Harvest Contact Emails' : 'Знайти Email-пошти'}</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Results section */}
          {gmailResults.length > 0 && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-slate-50 p-4 border border-slate-200 rounded-xl">
                <div>
                  <h3 className="text-xs font-bold text-slate-800 uppercase flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <span>
                      {isPl
                        ? `Znaleziono ${gmailResults.length} unikalnych kontaktów email (${targetRegion === 'poland' ? '🇵🇱 Polska' : targetRegion === 'eu' ? '🇪🇺 UE' : '🇺🇦 UA'})`
                        : isEn
                        ? `Found ${gmailResults.length} unique contact emails (${targetRegion === 'poland' ? '🇵🇱 Poland' : targetRegion === 'eu' ? '🇪🇺 EU' : '🇺🇦 Ukraine'})`
                        : `Знайдено ${gmailResults.length} унікальних контактних пошт (${targetRegion === 'poland' ? '🇵🇱 Польща' : targetRegion === 'eu' ? '🇪🇺 ЄС' : '🇺🇦 Україна'})`}
                    </span>
                  </h3>
                  {gmailSearchSummary && (
                    <p className="text-[11px] text-slate-500 mt-1">{gmailSearchSummary}</p>
                  )}
                </div>

                <div className="flex flex-wrap gap-2 items-center">
                  <button
                    onClick={() => copyToClipboard(gmailResults.map(l => l.email).join(', '))}
                    className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-100 text-slate-700 text-xs font-bold rounded flex items-center gap-1.5 cursor-pointer"
                  >
                    {copiedAll ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                    <span>{copiedAll ? (isPl ? 'Skopiowano!' : isEn ? 'Copied!' : 'Скопійовано!') : (isPl ? 'Kopiuj wszystkie' : isEn ? 'Copy All' : 'Копіювати всі пошти')}</span>
                  </button>

                  <button
                    onClick={downloadGmailCsv}
                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded flex items-center gap-1.5 cursor-pointer"
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span>Експорт CSV</span>
                  </button>

                  <button
                    onClick={() => handleSendInvitation(gmailResults.map(l => l.email))}
                    disabled={isSendingAll}
                    className="px-3.5 py-1.5 bg-red-600 hover:bg-red-700 disabled:bg-slate-300 text-white text-xs font-bold rounded flex items-center gap-1.5 cursor-pointer shadow-xs transition-all"
                    title={
                      invitationLanguage === 'pl'
                        ? 'Wyślij oficjalne zaproszenie w języku polskim do wszystkich znalezionych kontaktów'
                        : invitationLanguage === 'en'
                        ? 'Send official invitation in English to all discovered contacts'
                        : 'Розіслати офіційну пропозицію українською мовою усім знайденим поштам'
                    }
                  >
                    {isSendingAll ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    <span>
                      {isSendingAll
                        ? (isPl ? 'Wysyłanie...' : isEn ? 'Sending...' : 'Розсилка...')
                        : (isPl ? `Wyślij zaproszenie do wszystkich (${gmailResults.length}) [🇵🇱 PL]` : isEn ? `Broadcast to All (${gmailResults.length}) [🇬🇧 EN]` : `Розіслати пропозицію усім (${gmailResults.length}) [${invitationLanguage.toUpperCase()}]`)}
                    </span>
                  </button>
                </div>
              </div>

              {/* Table of Leads with horizontal scroll for mobile phones */}
              <div className="border border-slate-200 rounded-xl bg-white shadow-xs overflow-hidden">
                <div className="sm:hidden px-3 py-1.5 bg-red-50 border-b border-red-100 text-[10px] text-red-600 font-bold flex items-center justify-between">
                  <span>↔️ Гортайте таблицю вбік (Swipe horizontal)</span>
                  <span>{gmailResults.length} пошт</span>
                </div>
                <div className="overflow-x-auto w-full touch-pan-x scrollbar-thin scrollbar-thumb-slate-300">
                  <table className="w-full text-left border-collapse text-xs min-w-[780px]">
                    <thead>
                      <tr className="bg-slate-50 text-slate-500 font-bold uppercase tracking-wider text-[10px] border-b border-slate-200">
                        <th className="p-3 w-10">#</th>
                        <th className="p-3 min-w-[180px]">Email Пошта</th>
                        <th className="p-3 min-w-[120px]">{isPl ? 'Rynek / Język' : isEn ? 'Region / Lang' : 'Регіон / Мова'}</th>
                        <th className="p-3 min-w-[140px]">{isPl ? 'Nazwa / Stanowisko' : isEn ? 'Name / Title' : 'Ім\'я / Посада'}</th>
                        <th className="p-3 min-w-[140px]">{isPl ? 'Lokalizacja / Branża' : isEn ? 'Location / Sector' : 'Локація / Фах'}</th>
                        <th className="p-3 min-w-[180px]">{isPl ? 'Źródło i kontekst' : isEn ? 'Source & Snippet' : 'Джерело та контекст'}</th>
                        <th className="p-3 text-right min-w-[160px]">{isPl ? 'Akcje' : isEn ? 'Actions' : 'Дії'}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {gmailResults.map((lead, idx) => {
                        const leadReg = lead.region || targetRegion;
                        const leadLang = lead.language || (leadReg === 'poland' ? 'pl' : leadReg === 'eu' ? 'en' : 'uk');
                        const isSent = Boolean(sentMap[lead.email]);

                        return (
                          <tr key={idx} className="hover:bg-slate-50/70 transition-colors">
                            <td className="p-3 text-slate-400 font-bold text-[10px]">{idx + 1}</td>
                            <td className="p-3 font-bold text-red-600 flex items-center gap-1.5 whitespace-nowrap">
                              <Mail className="w-3.5 h-3.5 text-red-400 shrink-0" />
                              <span className="select-all">{lead.email}</span>
                            </td>

                            {/* Interactive Region & Language Badge */}
                            <td className="p-3 whitespace-nowrap">
                              <button
                                type="button"
                                onClick={() => handleToggleLeadRegion(idx)}
                                className={`px-2 py-1 rounded-md text-[10px] font-bold border transition-all flex items-center gap-1 cursor-pointer shadow-2xs ${
                                  leadReg === 'poland'
                                    ? 'bg-rose-50 border-rose-300 text-rose-700 hover:bg-rose-100'
                                    : leadReg === 'eu'
                                    ? 'bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100'
                                    : 'bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100'
                                }`}
                                title={
                                  isUk
                                    ? "Клікніть, щоб змінити регіон та мову листа (UA ↔ PL ↔ EU)"
                                    : isPl
                                    ? "Kliknij, aby zmienić rynek i język zaproszenia (PL ↔ EU ↔ UA)"
                                    : "Click to toggle target market and invitation language (EU ↔ UA ↔ PL)"
                                }
                              >
                                <span>{leadReg === 'poland' ? '🇵🇱 PL' : leadReg === 'eu' ? '🇪🇺 EU' : '🇺🇦 UA'}</span>
                                <span className="text-[9px] opacity-75 font-mono underline">
                                  {leadLang.toUpperCase()}
                                </span>
                              </button>
                            </td>

                            <td className="p-3 font-semibold text-slate-800">
                              {lead.name_or_title || '—'}
                            </td>
                            <td className="p-3 text-slate-600 text-[11px]">
                              <span className="inline-block bg-slate-100 px-2 py-0.5 rounded border border-slate-200 whitespace-nowrap">
                                {lead.city_or_region || region} • {lead.profession || profession}
                              </span>
                            </td>
                            <td className="p-3 max-w-xs">
                              <p className="text-[11px] text-slate-600 line-clamp-2">{lead.snippet}</p>
                              {lead.source_url && (
                                <a
                                  href={lead.source_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-[10px] text-blue-600 hover:underline flex items-center gap-1 mt-1 break-all"
                                >
                                  <span>{safeHostname(lead.source_url)}</span>
                                  <ExternalLink className="w-2.5 h-2.5 shrink-0" />
                                </a>
                              )}
                            </td>
                            <td className="p-3 text-right whitespace-nowrap">
                              <div className="flex items-center justify-end gap-1.5">
                                <button
                                  onClick={() => copyToClipboard(lead.email, idx)}
                                  className="p-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded transition-all cursor-pointer"
                                  title="Копіювати пошту"
                                >
                                  {copiedIndex === idx ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                                </button>

                                {isSent ? (
                                  <span className="inline-flex items-center gap-1 text-[10px] text-emerald-600 font-bold bg-emerald-50 px-2 py-1 rounded border border-emerald-200 shrink-0">
                                    <Check className="w-3 h-3" />
                                    <span>{isPl ? `Wysłano [${leadLang.toUpperCase()}]` : isEn ? `Sent [${leadLang.toUpperCase()}]` : `Надіслано [${leadLang.toUpperCase()}]`}</span>
                                  </span>
                                ) : (
                                  <button
                                    onClick={() => handleSendInvitation([lead.email], idx)}
                                    disabled={sendingEmailIndex === idx}
                                    className="px-2.5 py-1 bg-red-50 hover:bg-red-600 text-red-600 hover:text-white border border-red-200 text-[10px] font-bold rounded transition-all cursor-pointer flex items-center gap-1 shrink-0"
                                    title={
                                      leadLang === 'pl'
                                        ? 'Wyślij oficjalne zaproszenie w języku polskim'
                                        : leadLang === 'en'
                                        ? 'Send official invitation in English'
                                        : 'Надіслати офіційну пропозицію українською мовою'
                                    }
                                  >
                                    {sendingEmailIndex === idx ? (
                                      <RefreshCw className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <Send className="w-3 h-3" />
                                    )}
                                    <span>
                                      {leadLang === 'pl'
                                        ? 'Wyślij [🇵🇱 PL]'
                                        : leadLang === 'en'
                                        ? 'Send [🇬🇧 EN]'
                                        : 'Надіслати [🇺🇦 UA]'}
                                    </span>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {hasSearchedGmail && gmailResults.length === 0 && !isHarvestingGmail && (
            <div className="bg-white border border-slate-200 rounded-xl p-8 text-center space-y-3 shadow-xs">
              <AlertCircle className="w-8 h-8 text-amber-500 mx-auto" />
              <h4 className="text-xs font-bold text-slate-800 uppercase">
                {isPl ? 'Nie znaleziono kontaktów dla tego kryterium' : isEn ? 'No contacts found for this search criteria' : 'Контактів не знайдено за цим критерієм'}
              </h4>
              <p className="text-xs text-slate-500 max-w-md mx-auto leading-relaxed">
                {gmailSearchSummary || (isPl ? "Spróbuj zmienić specjalizację lub miasto (np. 'biuro rachunkowe Warszawa', 'radca prawny Kraków')." : "Спробуйте змінити фах або регіон.")}
              </p>
            </div>
          )}
        </div>
      )}

      {/* TAB 2: FORUMS SEARCH */}
      {activeTab === 'forums' && (
        <div className="space-y-6">
          
          {/* Controls Form */}
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-xs space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-3">
              <h3 className="text-xs font-bold uppercase text-slate-800 tracking-wider flex items-center gap-2">
                <Globe className="w-4 h-4 text-red-600" />
                <span>
                  {isPl
                    ? 'Wyszukiwanie forów dyskusyjnych, portali i grup branżowych'
                    : isEn
                    ? 'Search Industry Forums & Professional Communities'
                    : 'Пошук тематичних форумів, порталів та дошок оголошень'}
                </span>
              </h3>

              <div className="flex items-center gap-1.5">
                <span className="text-[10px] uppercase font-bold text-slate-400">
                  {isPl ? 'Rynek:' : isEn ? 'Market:' : 'Ринок:'}
                </span>
                <span className="px-2.5 py-0.5 rounded text-[11px] font-bold bg-slate-100 border border-slate-300 text-slate-800">
                  {targetRegion === 'poland' ? '🇵🇱 Polska' : targetRegion === 'eu' ? '🇪🇺 European Union' : '🇺🇦 Україна'}
                </span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 items-end">
              <div className="flex-1 space-y-1.5 w-full">
                <label className="text-[10px] font-bold uppercase text-slate-500">
                  {isPl ? 'Tematyka społeczności / Forum branżowego' : isEn ? 'Forum Topic / Industry Community' : 'Тематика спільноти / Форуму'}
                </label>
                <input
                  type="text"
                  value={forumTopic}
                  onChange={(e) => setForumTopic(e.target.value)}
                  list="forum-topic-options"
                  placeholder={
                    targetRegion === 'poland'
                      ? 'np. księgowość, prawo pracy, budownictwo, transport...'
                      : targetRegion === 'eu'
                      ? 'e.g. EU corporate tax, international law, FIDIC engineering...'
                      : 'напр. бухгалтерія, юридичний форум, будівництво...'
                  }
                  className="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2 text-xs font-mono text-slate-800 focus:outline-none focus:border-red-500"
                />
                <datalist id="forum-topic-options">
                  {forumPresets[targetRegion].map((topic, i) => (
                    <option key={i} value={topic} />
                  ))}
                </datalist>
              </div>

              <button
                onClick={handleHarvestForums}
                disabled={isHarvestingForums}
                className="w-full sm:w-auto px-6 py-2 bg-red-600 hover:bg-red-700 disabled:bg-slate-300 text-white font-bold text-xs uppercase rounded-lg shadow-sm transition-all flex items-center justify-center gap-2 cursor-pointer shrink-0"
              >
                {isHarvestingForums ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>{isPl ? 'Wyszukiwanie...' : isEn ? 'Searching...' : 'Пошук форумів...'}</span>
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4" />
                    <span>{isPl ? 'Znajdź fora' : isEn ? 'Find Forums' : 'Знайти форуми'}</span>
                  </>
                )}
              </button>
            </div>

            {/* Quick Forum Presets */}
            <div className="space-y-1.5 pt-1">
              <label className="text-[10px] font-bold uppercase text-slate-400">
                {isPl ? '💡 Sugerowane tematy dla wybranego rynku:' : isEn ? '💡 Suggested topics for selected geography:' : '💡 Рекомендовані тематики:'}
              </label>
              <div className="flex flex-wrap gap-1.5">
                {forumPresets[targetRegion].map((topic, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setForumTopic(topic)}
                    className="px-2.5 py-1 text-[11px] bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md border border-slate-200 transition-colors cursor-pointer"
                  >
                    + {topic}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Results section */}
          {forumResults.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between bg-slate-50 p-4 border border-slate-200 rounded-xl">
                <h3 className="text-xs font-bold text-slate-800 uppercase flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span>
                    {isPl
                      ? `Znaleziono ${forumResults.length} tematycznych platform (${targetRegion === 'poland' ? '🇵🇱 Polska' : targetRegion === 'eu' ? '🇪🇺 UE' : '🇺🇦 UA'})`
                      : isEn
                      ? `Found ${forumResults.length} community platforms (${targetRegion === 'poland' ? '🇵🇱 Poland' : targetRegion === 'eu' ? '🇪🇺 EU' : '🇺🇦 Ukraine'})`
                      : `Знайдено ${forumResults.length} тематичних ресурсів (${targetRegion === 'poland' ? '🇵🇱 Польща' : targetRegion === 'eu' ? '🇪🇺 ЄС' : '🇺🇦 Україна'})`}
                  </span>
                </h3>

                <button
                  onClick={downloadForumsCsv}
                  className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded flex items-center gap-1.5 cursor-pointer"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>Експорт CSV</span>
                </button>
              </div>

              <div className="sm:hidden px-3 py-1.5 bg-red-50 border border-red-100 rounded-lg text-[10px] text-red-600 font-bold flex items-center justify-between">
                <span>↔️ Гортайте картки форумів убік (Swipe horizontal)</span>
                <span>{forumResults.length} форумів</span>
              </div>

              <div className="flex md:grid grid-cols-1 md:grid-cols-2 gap-4 overflow-x-auto md:overflow-visible pb-3 md:pb-0 touch-pan-x scrollbar-thin scrollbar-thumb-slate-300">
                {forumResults.map((forum, idx) => (
                  <div key={idx} className="bg-white border border-slate-200 rounded-xl p-5 shadow-xs space-y-3 flex flex-col justify-between min-w-[280px] sm:min-w-[320px] md:min-w-0 shrink-0 md:shrink">
                    <div className="space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="font-bold text-sm text-slate-800 line-clamp-1">{forum.title}</h4>
                        <span className="px-2 py-0.5 text-[9px] bg-blue-50 text-blue-600 border border-blue-200 rounded font-bold uppercase shrink-0">
                          {forum.estimated_activity || 'Активний'}
                        </span>
                      </div>
                      <p className="text-xs text-slate-600 leading-relaxed">{forum.description}</p>
                    </div>

                    <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-[11px] gap-2">
                      <span className="text-slate-400 truncate">
                        {isPl ? 'Audytorium:' : isEn ? 'Audience:' : 'Аудиторія:'} {forum.audience_type || forumTopic}
                      </span>
                      <a
                        href={forum.url}
                        target="_blank"
                        rel="noreferrer"
                        className="px-3 py-1 bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 rounded font-bold flex items-center gap-1 transition-all shrink-0"
                      >
                        <span>{isPl ? 'Przejdź' : isEn ? 'Visit' : 'Перейти'}</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB 3: MASS IMPORT & INVITE */}
      {activeTab === 'import' && (
        <div className="space-y-6 animate-fade-in">
          <div className="bg-gradient-to-r from-slate-900 via-slate-950 to-slate-900 border border-slate-800 rounded-xl p-6 text-white shadow-lg space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-800 pb-4">
              <div className="space-y-1">
                <h3 className="text-sm font-bold uppercase tracking-wider text-red-400 flex items-center gap-2">
                  <Upload className="w-5 h-5 text-red-500" />
                  <span>
                    {isPl
                      ? 'Masowy import adresów email, wysyłka zaproszeń i zapis w bazie'
                      : isEn
                      ? 'Mass Email Import, Automated Invitations & Database Storage'
                      : 'Завантаження списків пошт, розсилка запрошень та збереження в базі'}
                  </span>
                </h3>
                <p className="text-xs text-slate-400">
                  {isPl
                    ? 'Wgraj plik (.txt, .csv, .xlsx) lub wklej listę adresów. System automatycznie zapisze je w bazie użytkowników i wyśle zaproszenia w wybranym języku (PL, EN lub UA).'
                    : isEn
                    ? 'Upload a file (.txt, .csv, .xlsx) or paste an email list. The system will save them to the user base and dispatch tailored invitations in your chosen language (PL, EN, or UA).'
                    : 'Завантажте файл (.txt, .csv, .xlsx) або вставте список пошт. Система автоматично додасть їх до бази користувачів та відправить запрошення з кнопками переходу.'}
                </p>
              </div>

              <label className="px-4 py-2.5 bg-red-600 hover:bg-red-500 text-white font-bold text-xs uppercase rounded-lg border border-red-500 transition-all flex items-center gap-2 cursor-pointer shadow shrink-0">
                <FileSpreadsheet className="w-4 h-4" />
                <span>📁 {isPl ? 'Wgraj plik (.txt / .csv)' : isEn ? 'Upload File (.txt / .csv)' : 'Завантажити файл (.txt / .csv)'}</span>
                <input type="file" accept=".txt,.csv,.xlsx,.xls" onChange={handleFileUpload} className="hidden" />
              </label>
            </div>

            {/* Language & Market selector for mass broadcast */}
            <div className="p-3.5 bg-slate-950/90 border border-slate-800 rounded-lg flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Languages className="w-4 h-4 text-emerald-400" />
                <span className="text-xs font-bold text-slate-300 uppercase">
                  {isPl ? 'Wybierz język wysyłanego zaproszenia:' : isEn ? 'Select Invitation Language for Broadcast:' : 'Мова тексту листа-запрошення:'}
                </span>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setInvitationLanguage('pl')}
                  className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition-all flex items-center gap-1 cursor-pointer ${
                    invitationLanguage === 'pl'
                      ? 'bg-rose-600 border-rose-400 text-white shadow ring-2 ring-rose-500/30'
                      : 'bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <span>🇵🇱</span>
                  <span>Polski (Polska / KSeF)</span>
                </button>

                <button
                  type="button"
                  onClick={() => setInvitationLanguage('en')}
                  className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition-all flex items-center gap-1 cursor-pointer ${
                    invitationLanguage === 'en'
                      ? 'bg-amber-600 border-amber-400 text-white shadow ring-2 ring-amber-500/30'
                      : 'bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <span>🇬🇧</span>
                  <span>English (EU / Global)</span>
                </button>

                <button
                  type="button"
                  onClick={() => setInvitationLanguage('uk')}
                  className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition-all flex items-center gap-1 cursor-pointer ${
                    invitationLanguage === 'uk'
                      ? 'bg-blue-600 border-blue-400 text-white shadow ring-2 ring-blue-500/30'
                      : 'bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <span>🇺🇦</span>
                  <span>Українська (UA / ДСТУ)</span>
                </button>
              </div>
            </div>

            {/* Textarea for emails */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-300 flex items-center gap-1.5">
                  <ListPlus className="w-4 h-4 text-emerald-400" />
                  <span>
                    {isPl ? 'Lista adresów email odbiorców:' : isEn ? 'Recipient Email Address List:' : 'Список email-адрес отримувачів:'}
                  </span>
                </label>
                {importText.trim() && (
                  <span className="px-2 py-0.5 text-[10px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 rounded">
                    🔍 {isPl ? 'Wykryto:' : isEn ? 'Found:' : 'Виявлено пошт:'} { (importText.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g) || []).length }
                  </span>
                )}
              </div>
              <textarea
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
                placeholder={
                  isPl
                    ? "Wklej listę adresów e-mail (jeden w linii lub rozdzielone przecinkami)...\nPrzykład:\nbiuro@rachunkowosc-warszawa.pl\njan.kowalski@gmail.com\nkancelaria@adwokat-krakow.pl"
                    : isEn
                    ? "Paste email list (one per line, or comma-separated)...\nExample:\nfinance@company.de\ntax.advisor@consultancy.eu\nprocurement@enterprise.com"
                    : "Вставте список електронних пошт (по 1 в рядок, або через кому / крапку з комою)...\nПриклад:\nivan.petrenko@gmail.com\nolga.koval@ukr.net\naccounting@company.com"
                }
                rows={7}
                className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-lg text-xs font-mono text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-red-500/50"
              />
            </div>

            {/* Custom Note input */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-300">
                {isPl ? 'Własna notatka / dopisek w treści wiadomości (opcjonalnie):' : isEn ? 'Custom note in email body (Optional):' : 'Персональна примітка у лист (опціонально):'}
              </label>
              <input
                type="text"
                value={customNoteInput}
                onChange={(e) => setCustomNoteInput(e.target.value)}
                placeholder={
                  isPl
                    ? "np. Dzień dobry! Dodajemy Państwa zespół do platformy analityki Deep Research Hub..."
                    : isEn
                    ? "e.g. Hello! We are granting your team complimentary trial access to Deep Research Hub..."
                    : "Наприклад: Вітаємо! Додаємо вас до системи аналітики для компанії ТОВ 'Агрокоспос'..."
                }
                className="w-full px-3.5 py-2 bg-slate-950 border border-slate-800 rounded-lg text-xs font-mono text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-red-500/50"
              />
            </div>

            {/* Action Controls */}
            <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
              <div className="flex items-center gap-2">
                <button
                  onClick={handleMassImportAndSend}
                  disabled={isImportingAndSending || !importText.trim()}
                  className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold text-xs uppercase rounded-lg shadow-md transition-all flex items-center gap-2 cursor-pointer"
                >
                  {isImportingAndSending ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>{isPl ? 'Wysyłanie i import...' : isEn ? 'Broadcasting & Importing...' : 'Виконується розсилка та імпорт...'}</span>
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4" />
                      <span>
                        {isPl
                          ? `✉️ Wyślij Zaproszenia [${invitationLanguage.toUpperCase()}] & Zapisz w Bazie`
                          : isEn
                          ? `✉️ Send Invitations [${invitationLanguage.toUpperCase()}] & Save to Users DB`
                          : `✉️ Надіслати Запрошення [${invitationLanguage.toUpperCase()}] & Додати до Бази Користувачів`}
                      </span>
                    </>
                  )}
                </button>

                {importText.trim() && (
                  <button
                    onClick={() => copyToClipboard(importText)}
                    className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold text-xs uppercase rounded-lg border border-slate-700 transition-all flex items-center gap-1.5 cursor-pointer"
                  >
                    {copiedAll ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                    <span>{copiedAll ? (isPl ? 'Skopiowano' : isEn ? 'Copied' : 'Скопійовано') : (isPl ? 'Kopiuj' : isEn ? 'Copy' : 'Копіювати')}</span>
                  </button>
                )}
              </div>

              {importText.trim() && (
                <button
                  onClick={() => { setImportText(''); setImportResults([]); }}
                  className="px-3 py-2 text-slate-400 hover:text-red-400 text-xs font-bold uppercase transition-all flex items-center gap-1 cursor-pointer"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>{isPl ? 'Wyczyść listę' : isEn ? 'Clear List' : 'Очистити список'}</span>
                </button>
              )}
            </div>
          </div>

          {/* Results Log Table */}
          {importResults.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-xs space-y-3">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-800 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span>
                    {isPl ? 'Raport z zapisu w bazie i wysyłki:' : isEn ? 'Database Save & Dispatch Report:' : 'Звіт про збереження в БД та відправку:'}
                  </span>
                </h4>
                <span className="text-xs font-bold text-slate-500">
                  {isPl ? 'Przetworzono:' : isEn ? 'Processed:' : 'Оброблено:'} {importResults.length}
                </span>
              </div>

              <div className="overflow-x-auto touch-pan-x border border-slate-200 rounded-lg">
                <table className="w-full text-left text-xs min-w-[600px]">
                  <thead className="bg-slate-100 text-slate-700 font-bold uppercase border-b border-slate-200">
                    <tr>
                      <th className="px-3 py-2">#</th>
                      <th className="px-3 py-2">Email</th>
                      <th className="px-3 py-2">{isPl ? 'Status w bazie' : isEn ? 'Users DB Status' : 'Статус у БД Users'}</th>
                      <th className="px-3 py-2">{isPl ? 'Język / Status wysyłki' : isEn ? 'Language / Dispatch Status' : 'Мова / Статус відправки'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-800">
                    {importResults.map((item, index) => (
                      <tr key={index} className="hover:bg-slate-50">
                        <td className="px-3 py-2 text-slate-400">{index + 1}</td>
                        <td className="px-3 py-2 font-bold text-slate-900">{item.email}</td>
                        <td className="px-3 py-2">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800">
                            <Check className="w-3 h-3" /> {isPl ? 'Zapisano w bazie ✅' : isEn ? 'Saved to DB ✅' : 'Додано у БД Users ✅'}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          {item.status === 'error' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-800">
                              ❌ {item.error || 'Помилка'}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-800">
                              ✉️ {item.loggedLocally ? (isPl ? 'Zapisano w logu' : isEn ? 'Logged locally' : 'Зафіксовано в лозі') : (isPl ? 'Pomyślnie wysłano' : isEn ? 'Successfully sent' : 'Успішно надіслано')}
                              <span className="ml-1 text-[9px] font-mono bg-blue-200/60 px-1 rounded uppercase">
                                {item.lang || invitationLanguage}
                              </span>
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* AUTOPILOT PROGRESS & EXECUTIVE REPORT MODAL */}
      {showAutopilotModal && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-50 flex items-center justify-center p-3 sm:p-6 overflow-y-auto">
          <div className="bg-white border border-slate-200 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden animate-scale-in">
            
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-slate-900 via-red-950 to-slate-900 text-white p-4 sm:p-5 flex items-center justify-between border-b border-slate-800 shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-600/30 border border-red-500/40 rounded-xl">
                  {isAutopilotRunning ? (
                    <RefreshCw className="w-5 h-5 text-amber-300 animate-spin" />
                  ) : (
                    <Award className="w-5 h-5 text-amber-400" />
                  )}
                </div>
                <div>
                  <h3 className="font-bold text-sm sm:text-base flex items-center gap-2 text-white">
                    <span>
                      {isAutopilotRunning
                        ? (isPl ? 'Autopilot: Wyszukiwanie & Wysyłka w toku...' : isEn ? 'Autopilot: Multi-Sector Search & Dispatch Running...' : '🚀 Автопілот: Пошук & Розсилка в процесі...')
                        : (isPl ? '🏆 Raport Końcowy Autopilota' : isEn ? '🏆 Autopilot Executive Summary Report' : '🏆 Підсумковий Звіт Автопілоту Розсилки')}
                    </span>
                    <span className="text-[10px] bg-red-600/80 px-2 py-0.5 rounded-full font-mono">
                      {targetRegion.toUpperCase()} / {invitationLanguage.toUpperCase()}
                    </span>
                  </h3>
                  <p className="text-[11px] text-slate-300">
                    {isAutopilotRunning
                      ? (isPl ? 'Proszę nie zamykać okna, trwa automatyczne przeszukiwanie sektorów i wysyłka zaproszeń.' : isEn ? 'Please keep this window open while the agent harvests and dispatches to each sector.' : 'Триває послідовний пошук контактів та автоматична відправка пропозицій за всіма галузями.')
                      : (isPl ? 'Kompleksowe podsumowanie wykonanych wyszukiwań i wysłanych zaproszeń.' : isEn ? 'Detailed breakdown of discovered contacts and dispatched proposals by sector.' : 'Деталізований звіт за кожним фахом: скільки пошт виявлено та успішно надіслано.')}
                  </p>
                </div>
              </div>

              {!isAutopilotRunning && (
                <button
                  type="button"
                  onClick={() => setShowAutopilotModal(false)}
                  className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors cursor-pointer"
                  title="Закрити"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* Modal Body */}
            <div className="p-4 sm:p-6 overflow-y-auto space-y-6">
              
              {/* LIVE PROGRESS STATE */}
              {isAutopilotRunning && (
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs font-bold text-slate-700">
                      <span className="flex items-center gap-1.5">
                        <Rocket className="w-4 h-4 text-red-600 animate-bounce" />
                        <span>{isPl ? 'Postęp cyklu:' : isEn ? 'Cycle Progress:' : 'Прогрес виконання:'}</span>
                      </span>
                      <span className="font-mono text-red-600">
                        {autopilotCurrentSectorIndex} / {autopilotTotalSectors || 10} ({Math.round(((autopilotCurrentSectorIndex) / (autopilotTotalSectors || 10)) * 100)}%)
                      </span>
                    </div>

                    {/* Progress bar */}
                    <div className="w-full bg-slate-100 rounded-full h-3.5 overflow-hidden border border-slate-200 p-0.5">
                      <div
                        className="bg-gradient-to-r from-red-600 via-rose-500 to-amber-500 h-full rounded-full transition-all duration-500 shadow-sm"
                        style={{ width: `${Math.max(5, (autopilotCurrentSectorIndex / (autopilotTotalSectors || 10)) * 100)}%` }}
                      />
                    </div>
                  </div>

                  {/* Current sector banner */}
                  <div className="p-4 bg-red-50/80 border border-red-200 rounded-xl space-y-2">
                    <div className="text-[11px] font-bold text-red-800 uppercase tracking-wider flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-red-600" />
                      <span>{isPl ? 'Bieżąca branża:' : isEn ? 'Current Sector:' : 'Поточна галузь:'}</span>
                    </div>
                    <div className="text-sm font-bold text-slate-900 bg-white p-2.5 rounded-lg border border-red-100 shadow-2xs">
                      {autopilotCurrentSectorName || (isPl ? 'Inicjalizacja...' : isEn ? 'Initializing...' : 'Ініціалізація...')}
                    </div>
                    <div className="text-xs text-slate-600 flex items-center gap-2 pt-1">
                      <RefreshCw className="w-3.5 h-3.5 animate-spin text-red-600 shrink-0" />
                      <span className="font-medium">{autopilotProgressStatus}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* REPORT WHEN COMPLETED */}
              {!isAutopilotRunning && autopilotReport && (
                <div className="space-y-6">
                  
                  {/* Summary Metric Cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="bg-emerald-50 border border-emerald-200 p-3.5 rounded-xl text-center space-y-1">
                      <div className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider flex items-center justify-center gap-1">
                        <Send className="w-3 h-3 text-emerald-600" />
                        <span>{isPl ? 'Wysłano zaproszeń' : isEn ? 'Dispatched' : 'Надіслано листів'}</span>
                      </div>
                      <div className="text-2xl font-black text-emerald-800 font-mono">
                        {autopilotReport.totalDispatched}
                      </div>
                      <div className="text-[10px] text-emerald-600 font-semibold">
                        {isPl ? 'Zaproszeń dostarczonych' : isEn ? 'Successfully sent' : 'Успішних відправок'}
                      </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 p-3.5 rounded-xl text-center space-y-1">
                      <div className="text-[10px] font-bold text-blue-700 uppercase tracking-wider flex items-center justify-center gap-1">
                        <Search className="w-3 h-3 text-blue-600" />
                        <span>{isPl ? 'Znaleziono kontaktów' : isEn ? 'Discovered' : 'Знайдено контактів'}</span>
                      </div>
                      <div className="text-2xl font-black text-blue-800 font-mono">
                        {autopilotReport.totalDiscovered}
                      </div>
                      <div className="text-[10px] text-blue-600 font-semibold">
                        {isPl ? 'Zidentyfikowanych w sieci' : isEn ? 'Public web emails' : 'Публічних пошт'}
                      </div>
                    </div>

                    <div className="bg-slate-50 border border-slate-200 p-3.5 rounded-xl text-center space-y-1">
                      <div className="text-[10px] font-bold text-slate-700 uppercase tracking-wider flex items-center justify-center gap-1">
                        <Briefcase className="w-3 h-3 text-slate-600" />
                        <span>{isPl ? 'Branż objętych' : isEn ? 'Sectors' : 'Галузей опрацьовано'}</span>
                      </div>
                      <div className="text-2xl font-black text-slate-800 font-mono">
                        {autopilotReport.sectors.length}
                      </div>
                      <div className="text-[10px] text-slate-500 font-semibold">
                        {isPl ? '100% zrealizowano' : isEn ? '100% completed' : '100% виконано'}
                      </div>
                    </div>

                    <div className="bg-amber-50 border border-amber-200 p-3.5 rounded-xl text-center space-y-1">
                      <div className="text-[10px] font-bold text-amber-800 uppercase tracking-wider flex items-center justify-center gap-1">
                        <Clock className="w-3 h-3 text-amber-600" />
                        <span>{isPl ? 'Czas realizacji' : isEn ? 'Duration' : 'Час виконання'}</span>
                      </div>
                      <div className="text-xs font-bold text-amber-900 font-mono pt-1">
                        {autopilotReport.startedAt} — {autopilotReport.completedAt}
                      </div>
                      <div className="text-[10px] text-amber-700 font-bold">
                        {autopilotReport.targetRegion.toUpperCase()} • {autopilotReport.language.toUpperCase()}
                      </div>
                    </div>
                  </div>

                  {/* Detailed Breakdown Table */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-2">
                        <BarChart3 className="w-4 h-4 text-red-600" />
                        <span>{isPl ? 'Szczegółowy podział wg kryteriów / branż:' : isEn ? 'Detailed Breakdown by Sector / Profession:' : 'Деталізація результатів за кожною галуззю:'}</span>
                      </h4>
                      <span className="text-[10px] text-slate-400 font-mono">
                        {autopilotReport.sectors.length} {isPl ? 'sektorów' : isEn ? 'sectors' : 'сфер'}
                      </span>
                    </div>

                    <div className="border border-slate-200 rounded-xl overflow-hidden shadow-xs">
                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs min-w-[650px]">
                          <thead>
                            <tr className="bg-slate-50 text-slate-600 font-bold uppercase tracking-wider text-[10px] border-b border-slate-200">
                              <th className="p-3 w-10">#</th>
                              <th className="p-3 min-w-[220px]">{isPl ? 'Kryterium / Branża' : isEn ? 'Sector / Profession' : 'Галузь / Критерій'}</th>
                              <th className="p-3 text-center min-w-[90px]">{isPl ? 'Znaleziono' : isEn ? 'Discovered' : 'Знайдено'}</th>
                              <th className="p-3 text-center min-w-[100px]">{isPl ? 'Wysłano' : isEn ? 'Dispatched' : 'Надіслано'}</th>
                              <th className="p-3 min-w-[130px]">{isPl ? 'Status' : isEn ? 'Status' : 'Статус'}</th>
                              <th className="p-3 min-w-[160px]">{isPl ? 'Szczegóły' : isEn ? 'Details' : 'Деталі'}</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {autopilotReport.sectors.map((sec, idx) => {
                              const isSuccess = sec.dispatchedCount > 0;
                              const isAlreadySent = sec.status === 'already_sent';
                              const isNoLeads = sec.status === 'no_leads';

                              return (
                                <tr key={idx} className="hover:bg-slate-50/70 transition-colors">
                                  <td className="p-3 text-slate-400 font-bold text-[10px]">{idx + 1}</td>
                                  <td className="p-3 font-semibold text-slate-900">
                                    <div className="flex items-center gap-2">
                                      <Briefcase className="w-3.5 h-3.5 text-red-500 shrink-0" />
                                      <span>{sec.sectorName}</span>
                                    </div>
                                  </td>
                                  <td className="p-3 text-center font-mono font-bold text-slate-700">
                                    {sec.discoveredCount}
                                  </td>
                                  <td className="p-3 text-center">
                                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-black font-mono ${
                                      sec.dispatchedCount > 0
                                        ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                                        : 'bg-slate-100 text-slate-500'
                                    }`}>
                                      {sec.dispatchedCount}
                                    </span>
                                  </td>
                                  <td className="p-3">
                                    {isSuccess && (
                                      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                                        <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                                        <span>{isPl ? 'Wysłano' : isEn ? 'Success' : 'Успішно'}</span>
                                      </span>
                                    )}
                                    {isAlreadySent && (
                                      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                                        <Check className="w-3 h-3 text-amber-600" />
                                        <span>{isPl ? 'Wcześniej wysłano' : isEn ? 'Already invited' : 'Вже запрошені'}</span>
                                      </span>
                                    )}
                                    {isNoLeads && (
                                      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                                        <span>{isPl ? 'Brak nowych' : isEn ? 'No new' : 'Без нових'}</span>
                                      </span>
                                    )}
                                    {sec.status === 'error' && (
                                      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-700 bg-red-50 px-2 py-0.5 rounded border border-red-200">
                                        <AlertCircle className="w-3 h-3 text-red-600" />
                                        <span>{isPl ? 'Błąd' : isEn ? 'Error' : 'Помилка'}</span>
                                      </span>
                                    )}
                                  </td>
                                  <td className="p-3 text-[11px] text-slate-500 max-w-xs truncate" title={sec.note || sec.dispatchedEmails.join(', ')}>
                                    {sec.note || (sec.dispatchedEmails.length > 0 ? sec.dispatchedEmails.join(', ') : '—')}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer / Actions */}
            {!isAutopilotRunning && autopilotReport && (
              <div className="bg-slate-50 p-4 sm:p-5 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3 shrink-0">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={downloadAutopilotReportCsv}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-lg transition-all flex items-center gap-1.5 cursor-pointer shadow-xs"
                  >
                    <Download className="w-4 h-4" />
                    <span>{isPl ? 'Pobierz Raport CSV' : isEn ? 'Download CSV Report' : 'Завантажити звіт CSV'}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      const textSummary = `🏆 ПІДСУМКОВИЙ ЗВІТ АВТОПІЛОТУ РОЗСИЛКИ
Ринок: ${autopilotReport.targetRegion.toUpperCase()} | Мова: ${autopilotReport.language.toUpperCase()}
Час: ${autopilotReport.startedAt} - ${autopilotReport.completedAt}
Всього знайдено пошт: ${autopilotReport.totalDiscovered}
Всього надіслано запрошень: ${autopilotReport.totalDispatched}
Галузей опрацьовано: ${autopilotReport.sectors.length}

ДЕТАЛІЗАЦІЯ ЗА ГАЛУЗЯМИ:
${autopilotReport.sectors.map((s, i) => `${i + 1}. ${s.sectorName} — Знайдено: ${s.discoveredCount} | Надіслано: ${s.dispatchedCount} | Статус: ${s.status}`).join('\n')}`;
                      copyToClipboard(textSummary);
                    }}
                    className="px-4 py-2 bg-white border border-slate-300 hover:bg-slate-100 text-slate-700 font-bold text-xs rounded-lg transition-all flex items-center gap-1.5 cursor-pointer"
                  >
                    <Copy className="w-4 h-4" />
                    <span>{isPl ? 'Kopiuj Raport' : isEn ? 'Copy Report' : 'Копіювати звіт'}</span>
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleRunAutopilot}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-bold text-xs rounded-lg transition-all flex items-center gap-1.5 cursor-pointer shadow-xs"
                  >
                    <Rocket className="w-4 h-4" />
                    <span>{isPl ? 'Uruchom ponownie' : isEn ? 'Rerun Autopilot' : 'Запустити повторно'}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setShowAutopilotModal(false)}
                    className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs rounded-lg transition-all cursor-pointer"
                  >
                    {isPl ? 'Zamknij' : isEn ? 'Close' : 'Закрити'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
